import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown,
  ChevronUp,
  Layers,
  Cpu,
  Radio,
  Play,
  Pause,
  Square,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  MapPin,
  MapPinned,
  BookOpen,
  Plus,
  Minus,
  Locate,
  Expand,
  UserPlus,
  User,
  Settings,
  ArrowLeft,
  MoreHorizontal,
  Info,
  X,
  Home,
  Crosshair,
} from 'lucide-react';
import type { MapContainerRef } from '../map/MapContainer';
import {
  getCampaignForMap,
  fetchCampaignZonesGeoJSON,
  fetchCampaignCollectionsGeoJSON,
  fetchCampaignDevices,
  fetchCampaignSessions,
  fetchMyActiveCampaignSession,
  startCampaignSession,
  endCampaignSession,
  claimCampaignZone,
  campaignStreamWebSocketUrl,
  gnssReceiverImpliesExternal,
  APIError,
  POSITION_SOURCE_BROWSER_GEOLOCATION,
  type CampaignSessionRow,
  type FieldDeviceSummary,
  type MyActiveCampaignSessionResponse,
  type SessionCollectionDevicePayload,
  type CampaignMapContextViewer,
} from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { useMapStore } from '../../stores/mapStore';
import { mapPositionToCoordinates } from '../../utils/geolocation';
import { requestCurrentPosition } from '../../utils/geolocationRequest';
import { resolveFieldMappingLocateTarget } from '../../utils/geo';
import { MARKETING_URL } from '../../lib/env';
import { setCampaignWorkspaceResumeIdInSession } from '../../utils/fieldCampaignsExploreSession';
import {
  isFieldCaptureLive,
  isSessionStatusActive,
  parseAuthNumericUserId,
} from '../../utils/authUserId';

const FIELD_COLLECTION_DOCS_URL = `${MARKETING_URL}/docs/console/campaigns`;
const FIELD_COLLECTION_DOCS_SYNC_URL = `${MARKETING_URL}/docs/console/campaigns#sync-live-updates`;

function initialsFromDisplayName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0][0] || '';
    const b = parts[parts.length - 1][0] || '';
    return `${a}${b}`.toUpperCase();
  }
  if (parts.length === 1 && parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return '?';
}

function viewerMayClaimZone(viewer: CampaignMapContextViewer | null): boolean {
  if (!viewer) return false;
  if (viewer.is_staff) return true;
  if (viewer.campaign_team_role?.trim()) return true;
  const r = (viewer.organization_role || '').toLowerCase();
  return r === 'owner' || r === 'admin';
}

/** Collectors can only select their own assigned zones; supervisors/admins/staff can select any. */
function viewerIsCollectorOnly(viewer: CampaignMapContextViewer | null): boolean {
  if (!viewer) return false;
  if (viewer.is_staff) return false;
  const orgRole = (viewer.organization_role || '').toLowerCase();
  if (orgRole === 'owner' || orgRole === 'admin') return false;
  const teamRole = (viewer.campaign_team_role || '').toLowerCase().trim();
  if (teamRole === 'supervisor' || teamRole === 'manager' || teamRole === 'admin') return false;
  // 'collector', 'field_worker', or any other non-supervisor team role → restricted
  return Boolean(teamRole);
}

function viewerRoleBadges(viewer: CampaignMapContextViewer | null): { key: string; label: string; className: string }[] {
  if (!viewer) return [];
  const out: { key: string; label: string; className: string }[] = [];
  const campaignRaw = viewer.campaign_team_role?.trim();
  if (campaignRaw) {
    const label = campaignRaw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    out.push({
      key: 'campaign-team',
      label,
      className:
        'border-cyan-300/90 bg-cyan-50 text-cyan-900 dark:border-cyan-400/50 dark:bg-cyan-500/20 dark:text-cyan-100',
    });
  }
  const o = (viewer.organization_role || '').toLowerCase();
  if (o) {
    let label: string;
    if (o === 'owner') label = 'Org owner';
    else if (o === 'admin') label = 'Org admin';
    else if (o === 'developer') label = 'Org developer';
    else label = 'Org member';
    out.push({
      key: 'org-role',
      label,
      className:
        'border-blue-300/90 bg-blue-50 text-blue-900 dark:border-blue-400/50 dark:bg-blue-500/20 dark:text-blue-100',
    });
  }
  if (viewer.is_staff) {
    out.push({
      key: 'staff',
      label: 'Staff',
      className:
        'border-violet-300/90 bg-violet-50 text-violet-900 dark:border-violet-400/50 dark:bg-violet-500/20 dark:text-violet-100',
    });
  }
  return out;
}

/** Match `MapContainer` campaign-zones paint — status-based zone colors. */
const ZONE_STATUS_COLORS: Record<string, string> = {
  unassigned: '#94a3b8',
  assigned: '#3b82f6',
  in_progress: '#f59e0b',
  completed: '#22c55e',
  needs_review: '#8b5cf6',
};
function zoneStatusColor(status?: string): string {
  return ZONE_STATUS_COLORS[(status || '').toLowerCase()] || ZONE_STATUS_COLORS.unassigned;
}

/** Match `MapContainer` campaign-live paint: points use amber pending / green approved / red rejected. */
const CAPTURE_STATUS_LEGEND = [
  { key: 'pending', label: 'Pending QA', color: '#f59e0b' },
  { key: 'approved', label: 'Approved', color: '#22c55e' },
  { key: 'rejected', label: 'Rejected', color: '#ef4444' },
] as const;

function OverlayToggleRow(props: {
  enabled: boolean;
  onToggle: () => void;
  disabled?: boolean;
  title: string;
}) {
  const { enabled, onToggle, disabled, title } = props;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => !disabled && onToggle()}
      className={clsx(
        'flex w-full items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-all shadow-sm',
        'bg-white dark:bg-gray-800/90',
        'border-slate-200/85 dark:border-gray-600/55',
        'hover:border-cyan-300/55 dark:hover:border-cyan-600/40',
        disabled && 'cursor-not-allowed opacity-45 hover:border-slate-200/85 dark:hover:border-gray-600/55',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold text-slate-900 dark:text-gray-50">{title}</div>
      </div>
      <span
        className={clsx(
          'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors',
          enabled
            ? 'bg-cyan-600 border-cyan-500 shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
            : 'bg-slate-200 border-slate-300 dark:bg-gray-700 dark:border-gray-600',
        )}
      >
        <span
          className={clsx(
            'pointer-events-none block h-5 w-5 rounded-full bg-white shadow-md transition-transform duration-200',
            enabled ? 'translate-x-[22px]' : 'translate-x-0.5',
          )}
        />
      </span>
    </button>
  );
}

function CompactInfoPopover(props: {
  panelId: string;
  activePanel: string | null;
  setActivePanel: (id: string | null) => void;
  label: string;
  children: ReactNode;
}) {
  const { panelId, activePanel, setActivePanel, label, children } = props;
  const open = activePanel === panelId;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const updatePosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn || !open) return;
    const r = btn.getBoundingClientRect();
    const margin = 12;
    const width = Math.min(288, Math.max(200, window.innerWidth - 2 * margin));
    let left = r.right - width;
    if (left < margin) left = margin;
    if (left + width > window.innerWidth - margin) {
      left = window.innerWidth - margin - width;
    }
    let top = r.bottom + 6;
    const h = popoverRef.current?.getBoundingClientRect().height ?? 0;
    if (h > 0 && top + h > window.innerHeight - margin) {
      top = Math.max(margin, r.top - h - 6);
    }
    setPos({ top, left, width });
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }

    const run = () => updatePosition();
    run();
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });

    const onWin = () => run();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setActivePanel(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, setActivePanel]);

  const portal =
    open &&
    pos &&
    createPortal(
      <div
        ref={popoverRef}
        role="dialog"
        aria-label={label}
        className="rounded-xl border border-slate-200 bg-white p-2.5 text-[11px] leading-relaxed text-slate-700 shadow-2xl dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
        style={{
          position: 'fixed',
          top: pos.top,
          left: pos.left,
          width: pos.width,
          zIndex: 200,
          maxHeight: 'min(70vh, 22rem)',
          overflowY: 'auto',
        }}
      >
        {children}
      </div>,
      document.body,
    );

  return (
    <div className="inline-flex shrink-0">
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setActivePanel(open ? null : panelId)}
        className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-200/90 hover:text-slate-800 dark:text-gray-400 dark:hover:bg-gray-600/45 dark:hover:text-gray-100"
      >
        <Info className="h-3.5 w-3.5" strokeWidth={2.25} />
      </button>
      {portal}
    </div>
  );
}

function maxSubmittedAt(features: GeoJSON.Feature[]): string | null {
  let max: string | null = null;
  for (const f of features) {
    const s = (f.properties as { submitted_at?: string } | null)?.submitted_at;
    if (s && (!max || s > max)) max = s;
  }
  return max;
}

function mergeLiveFeatures(
  prev: GeoJSON.FeatureCollection | null,
  delta: GeoJSON.FeatureCollection,
): GeoJSON.FeatureCollection {
  const byId = new Map<string, GeoJSON.Feature>();
  for (const f of prev?.features ?? []) {
    const id = f.id != null ? String(f.id) : null;
    if (id) byId.set(id, f);
  }
  for (const f of delta.features) {
    const id = f.id != null ? String(f.id) : null;
    if (id) byId.set(id, f);
  }
  return { type: 'FeatureCollection', features: Array.from(byId.values()) };
}

interface Props {
  campaignId: string;
  mapRef: React.RefObject<MapContainerRef | null>;
  isMapLoaded: boolean;
  isAuthenticated: boolean;
  userLocation: { lat: number; lng: number; accuracyM?: number | null } | null;
  onRequireAuth: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** From `?zone_id=` — map fits this zone once GeoJSON is loaded. */
  focusZoneId?: string | null;
  /** Clear the focused zone (updates URL). */
  onClearFocusedZone?: () => void;
  /** Select a zone (updates URL — same as tapping the map). */
  onSelectCampaignZone?: (zoneId: string, opts?: { sessionRestore?: boolean }) => void;
  /** Tighter bottom inset when the home bottom sheet is hidden (campaign-only layout). */
  compactDock?: boolean;
  /** Campaign field layout hides the left FAB — use header actions instead. */
  onOpenProfile?: () => void;
  onOpenSettings?: () => void;
  /** Return to normal explore map (clears `?campaign_id=`). */
  onExitCampaign?: () => void;
  /** Exit campaign and open the main map assignments picker (switch campaign). */
  onBrowseFieldCampaigns?: () => void;
  /**
   * Fired when the current user has an active collection session in this campaign.
   * Parent can disable map zone changes until the session ends.
   */
  onFieldSessionActiveChange?: (active: boolean) => void;
  /** Fired when live GPS capture is allowed (active session + tracking + published campaign). */
  onFieldCaptureLiveChange?: (live: boolean) => void;
  /** Open explore home while keeping the campaign session (for Contribute, search, etc.). */
  onGoHome?: () => void;
  /** Open the contribution flow; pass captureLiveLocation to seed GPS from the active session. */
  onOpenContribution?: (opts?: { captureLiveLocation?: boolean }) => void;
  /** When true, only the compact session dock is shown (explore chrome is visible behind). */
  homeOverlayMode?: boolean;
  /** Return from home overlay to the full campaign workspace. */
  onReturnToWorkspace?: () => void;
  /** Hide the bottom session dock (e.g. while contribution panel is open). */
  hideSessionDock?: boolean;
  /** Show Enable Location pill instead of alert() when locate fails. */
  onRequestLocationPrompt?: () => void;
}

/**
 * Session audit row: align `collection_device` with the registry record when a
 * `field_device_id` is used; otherwise describe browser Geolocation (public map).
 */
function buildSessionCollectionDevice(opts: {
  fieldDeviceId: string | undefined;
  registeredDevice: FieldDeviceSummary | undefined;
  userLocation: Props['userLocation'];
}): SessionCollectionDevicePayload {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
  const isMobileUa = typeof navigator !== 'undefined' && /Mobi|Android/i.test(navigator.userAgent);
  const clientLabel = isMobileUa ? 'Mobile browser (public map)' : 'Desktop browser (public map)';
  const accuracy =
    opts.userLocation?.accuracyM != null && Number.isFinite(opts.userLocation.accuracyM)
      ? opts.userLocation.accuracyM
      : undefined;

  if (opts.fieldDeviceId && opts.registeredDevice) {
    const d = opts.registeredDevice;
    const heartbeatSrc = d.last_gnss_position_source?.trim().toLowerCase();
    const externalFromRegistry = gnssReceiverImpliesExternal(d.gnss_receiver);
    const gnssSource =
      heartbeatSrc === 'internal' || heartbeatSrc === 'external'
        ? heartbeatSrc
        : externalFromRegistry
          ? 'external'
          : 'internal';
    return {
      is_registered_asset: true,
      device_label: [d.device_tag, d.device_model].filter(Boolean).join(' · ') || d.device_tag,
      device_model: d.device_model ?? undefined,
      client_user_agent: ua,
      gnss_source: gnssSource,
      gnss_receiver_label: d.gnss_receiver ?? undefined,
      gnss_connection: d.gnss_connection ?? undefined,
      horizontal_accuracy_m: accuracy,
    };
  }

  if (opts.fieldDeviceId) {
    return {
      is_registered_asset: true,
      device_label: `Registered device (${opts.fieldDeviceId.slice(0, 8)}…)`,
      client_user_agent: ua,
      gnss_source: 'internal',
      gnss_receiver_label: undefined,
      horizontal_accuracy_m: accuracy,
    };
  }

  return {
    is_registered_asset: false,
    device_label: clientLabel,
    client_user_agent: ua,
    gnss_source: 'internal',
    gnss_receiver_label: 'browser_geolocation',
    horizontal_accuracy_m: accuracy,
  };
}

function devicePresence(d: FieldDeviceSummary): { label: string; tone: string } {
  if (!d.last_seen_at) {
    return { label: 'Never seen', tone: 'text-slate-500 dark:text-gray-500' };
  }
  const ts = new Date(d.last_seen_at).getTime();
  const ageMin = (Date.now() - ts) / 60000;
  if (ageMin > 120) {
    return { label: 'Offline', tone: 'text-amber-700 dark:text-amber-400' };
  }
  if (ageMin > 15) {
    return { label: 'Idle', tone: 'text-slate-500 dark:text-gray-400' };
  }
  return { label: 'Recent', tone: 'text-emerald-700 dark:text-emerald-400' };
}

function featureCampaignZoneId(f: GeoJSON.Feature): string {
  if (f.id != null && f.id !== '') return String(f.id);
  return String((f.properties as { zone_id?: string } | null)?.zone_id ?? '');
}

export function CampaignWorkspacePanel({
  campaignId,
  mapRef,
  isMapLoaded,
  isAuthenticated,
  userLocation,
  onRequireAuth,
  collapsed = false,
  onToggleCollapse,
  focusZoneId = null,
  onClearFocusedZone,
  onSelectCampaignZone,
  compactDock = true,
  onOpenProfile,
  onOpenSettings,
  onExitCampaign,
  onBrowseFieldCampaigns,
  onFieldSessionActiveChange,
  onFieldCaptureLiveChange,
  onGoHome,
  onOpenContribution,
  homeOverlayMode = false,
  onReturnToWorkspace,
  hideSessionDock = false,
  onRequestLocationPrompt,
}: Props) {
  const [campaignName, setCampaignName] = useState<string>('');
  const [campaignDescription, setCampaignDescription] = useState<string | null>(null);
  const [districtName, setDistrictName] = useState<string | null>(null);
  const [organizationLogoUrl, setOrganizationLogoUrl] = useState<string | null>(null);
  const [orgLogoLoadFailed, setOrgLogoLoadFailed] = useState(false);
  const [mapContextViewer, setMapContextViewer] = useState<CampaignMapContextViewer | null>(null);
  const [campaignStatus, setCampaignStatus] = useState<string | null>(null);
  const [zonesFc, setZonesFc] = useState<GeoJSON.FeatureCollection | null>(null);
  const [liveFc, setLiveFc] = useState<GeoJSON.FeatureCollection | null>(null);
  const [showZones, setShowZones] = useState(true);
  const [showLive, setShowLive] = useState(true);
  /** When a zone is selected, optionally draw only that polygon to reduce clutter. */
  const [zoneBoundaryScope, setZoneBoundaryScope] = useState<'all' | 'selected'>('all');
  const [devices, setDevices] = useState<FieldDeviceSummary[]>([]);
  const [sessions, setSessions] = useState<CampaignSessionRow[]>([]);
  const [globalActiveSession, setGlobalActiveSession] = useState<CampaignSessionRow | null>(null);
  const [globalActiveCampaignId, setGlobalActiveCampaignId] = useState<string | null>(null);
  const [globalActiveCampaignName, setGlobalActiveCampaignName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionBusy, setSessionBusy] = useState(false);
  const [claimZoneBusy, setClaimZoneBusy] = useState(false);
  const [liveConnected, setLiveConnected] = useState(false);
  const [liveGpsPaused, setLiveGpsPaused] = useState(false);
  const liveSinceRef = useRef<string | null>(null);
  const zoneMapHighlightTimerRef = useRef<number | null>(null);
  const sessionGeoWatchRef = useRef<number | null>(null);
  const [fieldDevicePolicy, setFieldDevicePolicy] = useState<
    'none' | 'assigned_device' | 'external_gnss'
  >('none');
  const [selectedFieldDeviceId, setSelectedFieldDeviceId] = useState<string | null>(null);
  const [hardwareExpanded, setHardwareExpanded] = useState(false);
  const [mapOverlaysExpanded, setMapOverlaysExpanded] = useState(false);
  const [zoneSectionExpanded, setZoneSectionExpanded] = useState(false);
  const [recentSessionsExpanded, setRecentSessionsExpanded] = useState(false);
  const [sessionSyncInstructionOpen, setSessionSyncInstructionOpen] = useState(false);
  const [zoneFlyIndicatorPulse, setZoneFlyIndicatorPulse] = useState(false);
  const authUser = useAuthStore((s) => s.user);
  const myUserId = parseAuthNumericUserId(authUser?.id);
  const setUserLocation = useMapStore((s) => s.setUserLocation);
  const setIsLocating = useMapStore((s) => s.setIsLocating);
  const isLocating = useMapStore((s) => s.isLocating);
  const sheetState = useMapStore((s) => s.sheetState);
  const setContributionSheetChromeBottom = useMapStore((s) => s.setContributionSheetChromeBottom);
  const campaignChromeRootRef = useRef<HTMLDivElement>(null);

  /** Keep zoom / GPS pills above the field campaign panel (same store as Add Location). */
  useLayoutEffect(() => {
    if (homeOverlayMode || hideSessionDock) {
      setContributionSheetChromeBottom(null);
      return;
    }
    const el = campaignChromeRootRef.current;
    if (!el) return;

    const CHROME_GAP_PX = 8;
    const updateChromeBottom = () => {
      const rect = el.getBoundingClientRect();
      const insetPx = Math.max(0, window.innerHeight - rect.top + CHROME_GAP_PX);
      setContributionSheetChromeBottom(`${insetPx}px`);
    };

    updateChromeBottom();
    const observer = new ResizeObserver(updateChromeBottom);
    observer.observe(el);
    window.addEventListener('resize', updateChromeBottom);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateChromeBottom);
      setContributionSheetChromeBottom(null);
    };
  }, [
    collapsed,
    homeOverlayMode,
    hideSessionDock,
    setContributionSheetChromeBottom,
  ]);

  const homeOverlayDockBottom = useMemo(() => {
    const sheetOffset: Record<string, string> = {
      minimized: 'calc(2vh + 0.75rem)',
      collapsed: 'calc(8vh + 0.75rem)',
      half: 'calc(40vh + 0.75rem)',
      expanded: 'calc(85vh + 0.75rem)',
    };
    return sheetOffset[sheetState] ?? '0.75rem';
  }, [sheetState]);

  const refreshGlobalActiveSession = useCallback(async (): Promise<MyActiveCampaignSessionResponse | null> => {
    if (!isAuthenticated) {
      setGlobalActiveSession(null);
      setGlobalActiveCampaignId(null);
      setGlobalActiveCampaignName(null);
      return null;
    }
    try {
      const res = await fetchMyActiveCampaignSession();
      if (res.session && isSessionStatusActive(res.session.status)) {
        setGlobalActiveSession({
          ...res.session,
          campaign_id: res.campaign_id,
          campaign_name: res.campaign_name,
          focused_zone_id: res.focused_zone_id,
        });
        setGlobalActiveCampaignId(res.campaign_id);
        setGlobalActiveCampaignName(res.campaign_name);
      } else {
        setGlobalActiveSession(null);
        setGlobalActiveCampaignId(null);
        setGlobalActiveCampaignName(null);
      }
      return res;
    } catch {
      setGlobalActiveSession(null);
      setGlobalActiveCampaignId(null);
      setGlobalActiveCampaignName(null);
      return null;
    }
  }, [isAuthenticated]);

  const restoreSessionZone = useCallback(
    (activeRes: MyActiveCampaignSessionResponse | null, currentZoneId: string | null | undefined) => {
      if (!activeRes?.session || !isSessionStatusActive(activeRes.session.status)) return;
      if (activeRes.campaign_id && activeRes.campaign_id !== campaignId) return;
      const zid = activeRes.focused_zone_id?.trim();
      if (!zid || zid === currentZoneId || !onSelectCampaignZone) return;
      onSelectCampaignZone(zid, { sessionRestore: true });
    },
    [campaignId, onSelectCampaignZone],
  );

  const myActiveSession = useMemo(() => {
    if (globalActiveSession && isSessionStatusActive(globalActiveSession.status)) {
      return globalActiveSession;
    }
    return (
      sessions.find(
        (s) =>
          isSessionStatusActive(s.status) &&
          myUserId != null &&
          s.user_id != null &&
          s.user_id === myUserId,
      ) ?? null
    );
  }, [globalActiveSession, sessions, myUserId]);

  const activeSessionCampaignId = globalActiveCampaignId ?? campaignId;
  const activeSessionOnOtherCampaign =
    Boolean(myActiveSession && isSessionStatusActive(myActiveSession.status)) &&
    globalActiveCampaignId != null &&
    globalActiveCampaignId !== campaignId;

  const sessionAppliesToThisCampaign =
    Boolean(myActiveSession && isSessionStatusActive(myActiveSession.status)) &&
    (!globalActiveCampaignId || globalActiveCampaignId === campaignId);

  const fieldCaptureLive = isFieldCaptureLive({
    sessionApplies: sessionAppliesToThisCampaign,
    campaignActive: campaignStatus === 'active',
    liveGpsPaused,
    externalGnssBlocked: fieldDevicePolicy === 'external_gnss',
  });

  const handleActivateActiveSession = useCallback(async () => {
    const activeRes = await refreshGlobalActiveSession();
    if (!activeRes?.session || !isSessionStatusActive(activeRes.session.status)) return;
    if (activeRes.campaign_id && activeRes.campaign_id !== campaignId) return;
    restoreSessionZone(activeRes, focusZoneId);
    setZoneSectionExpanded(true);
  }, [refreshGlobalActiveSession, campaignId, restoreSessionZone, focusZoneId]);

  const zoneSelectionLocked = sessionAppliesToThisCampaign;

  useEffect(() => {
    onFieldSessionActiveChange?.(sessionAppliesToThisCampaign);
  }, [sessionAppliesToThisCampaign, onFieldSessionActiveChange]);

  useEffect(() => {
    onFieldCaptureLiveChange?.(fieldCaptureLive);
  }, [fieldCaptureLive, onFieldCaptureLiveChange]);

  useEffect(() => {
    if (!sessionAppliesToThisCampaign || !myActiveSession) return;
    const zid = myActiveSession.focused_zone_id?.trim();
    if (!zid || zid === focusZoneId || !onSelectCampaignZone) return;
    onSelectCampaignZone(zid, { sessionRestore: true });
  }, [
    sessionAppliesToThisCampaign,
    myActiveSession?.id,
    myActiveSession?.focused_zone_id,
    focusZoneId,
    onSelectCampaignZone,
  ]);

  useEffect(() => {
    if (!isAuthenticated) return;
    void refreshGlobalActiveSession();
  }, [isAuthenticated, refreshGlobalActiveSession]);

  const [confirmExitCampaignOpen, setConfirmExitCampaignOpen] = useState(false);
  const [confirmEndSessionOpen, setConfirmEndSessionOpen] = useState(false);
  const [campaignMoreToolsOpen, setCampaignMoreToolsOpen] = useState(false);
  const [detailInfoPanel, setDetailInfoPanel] = useState<string | null>(null);
  const campaignMoreToolsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!campaignMoreToolsOpen) return;
    const onDown = (e: MouseEvent) => {
      if (campaignMoreToolsRef.current?.contains(e.target as Node)) return;
      setCampaignMoreToolsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [campaignMoreToolsOpen]);

  useEffect(() => {
    if (collapsed) {
      setCampaignMoreToolsOpen(false);
      setDetailInfoPanel(null);
    }
  }, [collapsed]);

  const requestExitCampaign = useCallback(() => {
    if (!onExitCampaign) return;
    setConfirmExitCampaignOpen(true);
  }, [onExitCampaign]);

  const confirmExitCampaign = useCallback(() => {
    setConfirmExitCampaignOpen(false);
    onExitCampaign?.();
  }, [onExitCampaign]);

  useEffect(() => {
    if (!focusZoneId) setZoneBoundaryScope('all');
  }, [focusZoneId]);

  const zoneFocusGeometry = useMemo(() => {
    if (!focusZoneId || !zonesFc?.features?.length) return null;
    for (const f of zonesFc.features) {
      if (!f.geometry) continue;
      const fid =
        f.id != null
          ? String(f.id)
          : (f.properties as { zone_id?: string } | null)?.zone_id;
      if (fid === focusZoneId) return f.geometry;
    }
    return null;
  }, [focusZoneId, zonesFc]);

  const focusedZoneMeta = useMemo(() => {
    if (!focusZoneId || !zonesFc?.features?.length) return null;
    for (const f of zonesFc.features) {
      const fid =
        f.id != null
          ? String(f.id)
          : (f.properties as { zone_id?: string } | null)?.zone_id;
      if (fid !== focusZoneId) continue;
      const p = (f.properties || {}) as {
        name?: string;
        status?: string;
        progress_pct?: number;
        color?: string;
        assigned_to_id?: number | null;
        assigned_to_name?: string | null;
      };
      const rawAid = p.assigned_to_id as number | string | null | undefined;
      const assignedToId =
        rawAid != null && String(rawAid).trim() !== '' && Number.isFinite(Number(rawAid))
          ? Number(rawAid)
          : null;
      return {
        name: p.name?.trim() || `Zone ${focusZoneId.slice(0, 8)}…`,
        status: p.status,
        progressPct: p.progress_pct,
        color: p.color,
        assignedToId,
        assignedToName: p.assigned_to_name?.trim() || null,
      };
    }
    return null;
  }, [focusZoneId, zonesFc]);

  const zoneListItems = useMemo(() => {
    if (!zonesFc?.features?.length) return [];
    const rows: Array<{
      id: string;
      name: string;
      color?: string;
      status?: string;
      assignedToId: number | null;
    }> = [];
    for (const f of zonesFc.features) {
      if (!f.geometry) continue;
      const id =
        f.id != null && f.id !== ''
          ? String(f.id)
          : String((f.properties as { zone_id?: string } | null)?.zone_id ?? '');
      if (!id) continue;
      const p = (f.properties || {}) as {
        name?: string;
        color?: string;
        status?: string;
        assigned_to_id?: number | string | null;
      };
      const rawAid = p.assigned_to_id as number | string | null | undefined;
      const assignedToId =
        rawAid != null && String(rawAid).trim() !== '' && Number.isFinite(Number(rawAid))
          ? Number(rawAid)
          : null;
      rows.push({
        id,
        name: p.name?.trim() || `Zone ${id.slice(0, 8)}…`,
        color: p.color,
        status: p.status,
        assignedToId,
      });
    }
    return rows;
  }, [zonesFc]);

  const myZoneItemsInCampaign = useMemo(() => {
    if (myUserId == null) return [];
    return zoneListItems.filter((z) => z.assignedToId === myUserId);
  }, [zoneListItems, myUserId]);

  const zonesLayerData = useMemo((): GeoJSON.FeatureCollection | null => {
    if (!showZones || !zonesFc?.features?.length) return null;
    if (!focusZoneId || zoneBoundaryScope !== 'selected') return zonesFc;
    const picked = zonesFc.features.filter((f) => featureCampaignZoneId(f) === focusZoneId);
    if (!picked.length) return zonesFc;
    return { type: 'FeatureCollection', features: picked };
  }, [zonesFc, showZones, focusZoneId, zoneBoundaryScope]);

  const viewerRoleBadgesList = useMemo(() => viewerRoleBadges(mapContextViewer), [mapContextViewer]);

  const viewerMayClaim = useMemo(() => viewerMayClaimZone(mapContextViewer), [mapContextViewer]);

  const isCollectorOnly = useMemo(() => viewerIsCollectorOnly(mapContextViewer), [mapContextViewer]);

  useEffect(() => {
    if (zoneSelectionLocked) return;
    if (!focusZoneId || !isCollectorOnly || !zonesFc?.features?.length) return;
    const myZoneIds = new Set(myZoneItemsInCampaign.map((z) => z.id));
    if (!myZoneIds.has(focusZoneId)) {
      onClearFocusedZone?.();
    }
  }, [zoneSelectionLocked, focusZoneId, isCollectorOnly, myZoneItemsInCampaign, zonesFc, onClearFocusedZone]);

  const focusedZoneClaimEligible = useMemo(() => {
    if (!focusedZoneMeta) return false;
    const st = (focusedZoneMeta.status || '').toLowerCase();
    if (st === 'completed' || st === 'needs_review') return false;
    return focusedZoneMeta.assignedToId == null;
  }, [focusedZoneMeta]);

  const mapOverlaysSummary = useMemo(() => {
    let zonesPart = 'n/a';
    if (zonesFc?.features?.length) {
      if (!showZones) zonesPart = 'off';
      else if (focusZoneId && zoneBoundaryScope === 'selected') zonesPart = 'on · this zone';
      else zonesPart = 'on';
    }
    const livePart = liveFc ? (showLive ? 'on' : 'off') : 'n/a';
    return `Zones ${zonesPart} · Captures ${livePart}`;
  }, [zonesFc, liveFc, showZones, showLive, focusZoneId, zoneBoundaryScope]);

  const flyToSelectedZoneOnMap = useCallback(() => {
    if (!isMapLoaded || !mapRef.current || !zoneFocusGeometry) return;
    try {
      if (zoneMapHighlightTimerRef.current != null) {
        clearTimeout(zoneMapHighlightTimerRef.current);
        zoneMapHighlightTimerRef.current = null;
      }
      mapRef.current.clearHighlight();
      mapRef.current.highlightGeometry(zoneFocusGeometry);
      mapRef.current.fitCampaignGeometry(zoneFocusGeometry);
      setZoneFlyIndicatorPulse(true);
      window.setTimeout(() => setZoneFlyIndicatorPulse(false), 2200);
      zoneMapHighlightTimerRef.current = window.setTimeout(() => {
        zoneMapHighlightTimerRef.current = null;
        try {
          mapRef.current?.clearHighlight();
        } catch {
          /* noop */
        }
      }, 4500);
    } catch {
      /* map style reload race */
    }
  }, [isMapLoaded, mapRef, zoneFocusGeometry]);

  useEffect(() => {
    if (zoneMapHighlightTimerRef.current != null) {
      clearTimeout(zoneMapHighlightTimerRef.current);
      zoneMapHighlightTimerRef.current = null;
    }
    setZoneFlyIndicatorPulse(false);
    try {
      mapRef.current?.clearHighlight();
    } catch {
      /* noop */
    }
  }, [focusZoneId, campaignId, mapRef]);

  useEffect(() => {
    liveSinceRef.current = null;
  }, [campaignId]);

  useEffect(() => {
    setCampaignStatus(null);
    setOrganizationLogoUrl(null);
    setOrgLogoLoadFailed(false);
    setMapContextViewer(null);
    setCampaignDescription(null);
  }, [campaignId]);

  const sessionDevicePool =
    myUserId == null || !Number.isFinite(myUserId)
      ? []
      : (() => {
        const mine = devices.filter((d) => d.assigned_to_id === myUserId);
        if (fieldDevicePolicy === 'external_gnss') {
          return mine.filter((d) => gnssReceiverImpliesExternal(d.gnss_receiver));
        }
        if (fieldDevicePolicy === 'assigned_device') return mine;
        return [];
      })();

  useEffect(() => {
    if (fieldDevicePolicy === 'none') {
      setSelectedFieldDeviceId(null);
      return;
    }
    if (sessionDevicePool.length === 1) {
      setSelectedFieldDeviceId(sessionDevicePool[0].id);
      return;
    }
    setSelectedFieldDeviceId((prev) =>
      prev && sessionDevicePool.some((d) => d.id === prev) ? prev : null,
    );
  }, [fieldDevicePolicy, sessionDevicePool]);

  const effectiveFieldDeviceId = useMemo(() => {
    if (fieldDevicePolicy === 'none') return undefined;
    if (sessionDevicePool.length === 1) return sessionDevicePool[0].id;
    return selectedFieldDeviceId || undefined;
  }, [fieldDevicePolicy, sessionDevicePool, selectedFieldDeviceId]);

  const sessionDeviceGateOk =
    fieldDevicePolicy === 'none' ||
    (sessionDevicePool.length > 0 && effectiveFieldDeviceId != null && effectiveFieldDeviceId !== '');

  /** Web map only has browser Geolocation; API rejects external_gnss without rover-backed position_source. */
  const publicMapBlocksExternalGnssSessions = fieldDevicePolicy === 'external_gnss';
  const campaignIsActive = campaignStatus === 'active';
  const showPausedCampaignSessionGate =
    !campaignIsActive && !loading && campaignStatus != null;
  const [sessionPausedDetailExpanded, setSessionPausedDetailExpanded] = useState(true);

  useEffect(() => {
    setSessionPausedDetailExpanded(!showPausedCampaignSessionGate);
  }, [showPausedCampaignSessionGate]);

  const sessionPausedBodyHidden =
    showPausedCampaignSessionGate && !sessionPausedDetailExpanded && !myActiveSession;

  const canStartSessionOnPublicMap =
    sessionDeviceGateOk &&
    !publicMapBlocksExternalGnssSessions &&
    campaignIsActive;

  const load = useCallback(async () => {
    if (!isAuthenticated || !campaignId) return;
    setLoading(true);
    setError(null);
    try {
      const [ctx, z, col, dev, sess] = await Promise.all([
        getCampaignForMap(campaignId),
        fetchCampaignZonesGeoJSON(campaignId),
        fetchCampaignCollectionsGeoJSON(campaignId, 500),
        fetchCampaignDevices(campaignId),
        fetchCampaignSessions(campaignId),
      ]);
      const activeRes = await refreshGlobalActiveSession();
      restoreSessionZone(activeRes, focusZoneId);
      const camp = ctx.campaign;
      setCampaignName(camp.name);
      const desc = camp.description?.trim();
      setCampaignDescription(desc || null);
      setDistrictName(camp.district_name ?? null);
      const logo = camp.organization_logo_url?.trim();
      setOrganizationLogoUrl(logo || null);
      setOrgLogoLoadFailed(false);
      setMapContextViewer(ctx.viewer);
      setCampaignStatus(camp.status);
      setFieldDevicePolicy(camp.field_device_requirement ?? 'none');
      setZonesFc(z.features?.length ? z : null);
      setLiveFc(col.features?.length ? col : null);
      liveSinceRef.current = maxSubmittedAt(col.features ?? []);
      setDevices(dev);
      setSessions(sess);
    } catch (e) {
      if (e instanceof APIError && e.status === 401) {
        setError('Sign in to view this campaign on the map.');
      } else if (e instanceof APIError && e.status === 403) {
        setError('You do not have access to this campaign.');
      } else {
        setError(e instanceof Error ? e.message : 'Failed to load campaign workspace');
      }
      setZonesFc(null);
      setLiveFc(null);
      liveSinceRef.current = null;
      setDevices([]);
      setSessions([]);
      setFieldDevicePolicy('none');
      setOrganizationLogoUrl(null);
      setOrgLogoLoadFailed(false);
      setMapContextViewer(null);
      setCampaignDescription(null);
      setCampaignStatus(null);
    } finally {
      setLoading(false);
    }
  }, [campaignId, isAuthenticated, refreshGlobalActiveSession, restoreSessionZone, focusZoneId]);

  const refreshZonesGeoJSONOnly = useCallback(async () => {
    if (!isAuthenticated || !campaignId) return;
    try {
      const z = await fetchCampaignZonesGeoJSON(campaignId);
      setZonesFc(z.features?.length ? z : null);
    } catch {
      /* keep existing zone layer */
    }
  }, [campaignId, isAuthenticated]);

  const handleClaimZone = useCallback(async () => {
    if (!focusZoneId || !campaignId || !isAuthenticated || claimZoneBusy) return;
    setClaimZoneBusy(true);
    try {
      await claimCampaignZone(campaignId, focusZoneId);
      await refreshZonesGeoJSONOnly();
    } catch (e) {
      const msg =
        e instanceof APIError ? e.message : e instanceof Error ? e.message : 'Could not claim zone';
      alert(msg);
    } finally {
      setClaimZoneBusy(false);
    }
  }, [focusZoneId, campaignId, isAuthenticated, claimZoneBusy, refreshZonesGeoJSONOnly]);

  const refreshLiveIncremental = useCallback(async () => {
    if (!isAuthenticated || !campaignId) return;
    const since = liveSinceRef.current;
    if (!since) return;
    try {
      const delta = await fetchCampaignCollectionsGeoJSON(campaignId, 500, since);
      if (!delta.features?.length) return;
      setLiveFc((prev) => {
        const merged = mergeLiveFeatures(prev, delta);
        liveSinceRef.current = maxSubmittedAt(merged.features);
        return merged.features.length ? merged : null;
      });
    } catch {
      /* ignore incremental errors */
    }
  }, [campaignId, isAuthenticated]);

  const bumpLiveLayer = useCallback(async () => {
    if (!isAuthenticated || !campaignId) return;
    if (!liveSinceRef.current) {
      try {
        const col = await fetchCampaignCollectionsGeoJSON(campaignId, 500);
        setLiveFc(col.features?.length ? col : null);
        liveSinceRef.current = maxSubmittedAt(col.features ?? []);
      } catch {
        /* ignore */
      }
      return;
    }
    await refreshLiveIncremental();
  }, [campaignId, isAuthenticated, refreshLiveIncremental]);

  useEffect(() => {
    if (!isAuthenticated || !campaignId) return;
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    let ws: WebSocket | null = null;
    let cancelled = false;
    try {
      ws = new WebSocket(campaignStreamWebSocketUrl(campaignId, token));
    } catch {
      return;
    }
    ws.onopen = () => {
      if (!cancelled) setLiveConnected(true);
    };
    ws.onclose = () => {
      if (!cancelled) setLiveConnected(false);
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data as string) as {
          type?: string;
          ts?: string;
          data?: {
            device_id?: string;
            pending_outbox_count?: number | null;
            last_sync_attempt_at?: string | null;
            battery_pct?: number | null;
          };
        };
        const t = msg?.type;
        if (t === 'contribution_created' || t === 'batch_sync') void bumpLiveLayer();
        if (t === 'device_telemetry' && msg.data?.device_id) {
          const payload = msg.data;
          setDevices((prev) =>
            prev.map((d) =>
              d.id === payload.device_id
                ? {
                  ...d,
                  pending_outbox_count: payload.pending_outbox_count ?? d.pending_outbox_count,
                  last_sync_attempt_at: payload.last_sync_attempt_at ?? d.last_sync_attempt_at,
                  last_battery_pct: payload.battery_pct ?? d.last_battery_pct,
                  last_seen_at: msg.ts ?? d.last_seen_at,
                }
                : d,
            ),
          );
        }
      } catch {
        /* ignore */
      }
    };
    return () => {
      cancelled = true;
      ws?.close();
    };
  }, [isAuthenticated, campaignId, bumpLiveLayer]);

  useEffect(() => {
    if (!isAuthenticated || !campaignId || !showLive) return;
    const id = window.setInterval(() => void bumpLiveLayer(), 55_000);
    return () => window.clearInterval(id);
  }, [isAuthenticated, campaignId, showLive, bumpLiveLayer]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (myActiveSession) setLiveGpsPaused(false);
  }, [myActiveSession?.id]);

  useEffect(() => {
    if (!myActiveSession || liveGpsPaused || publicMapBlocksExternalGnssSessions) {
      if (sessionGeoWatchRef.current != null && typeof navigator !== 'undefined' && 'geolocation' in navigator) {
        navigator.geolocation.clearWatch(sessionGeoWatchRef.current);
        sessionGeoWatchRef.current = null;
      }
      return;
    }
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) return;
    sessionGeoWatchRef.current = navigator.geolocation.watchPosition(
      (position) => {
        useMapStore.getState().setUserLocation(mapPositionToCoordinates(position));
      },
      () => {
        /* Leave last fix; user may fix permissions and resume. */
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20_000 },
    );
    return () => {
      if (sessionGeoWatchRef.current != null && 'geolocation' in navigator) {
        navigator.geolocation.clearWatch(sessionGeoWatchRef.current);
        sessionGeoWatchRef.current = null;
      }
    };
  }, [myActiveSession, liveGpsPaused, publicMapBlocksExternalGnssSessions]);

  useEffect(() => {
    if (!isMapLoaded || !mapRef.current) return;
    try {
      mapRef.current.setCampaignMapData({
        zones: zonesLayerData,
        live: showLive ? liveFc : null,
        focusGeometry: zoneFocusGeometry,
      });
    } catch {
      // map style reload race
    }
  }, [isMapLoaded, mapRef, zonesLayerData, liveFc, showLive, zoneFocusGeometry]);

  /** Explore map should not keep campaign zone / live overlays after leaving the workspace. */
  useEffect(() => {
    return () => {
      try {
        mapRef.current?.setCampaignMapData({ zones: null, live: null, fitBounds: false });
        mapRef.current?.clearHighlight();
      } catch {
        /* map teardown / style reload */
      }
    };
  }, [mapRef]);

  const handleStartSession = async () => {
    if (!isAuthenticated) {
      onRequireAuth();
      return;
    }
    setSessionBusy(true);
    setError(null);
    try {
      if (publicMapBlocksExternalGnssSessions) {
        setError(
          'This campaign uses external GNSS (rover-backed coordinates). Session start on the map will be available in Coordal Field — a native app coming soon. Until then, use campaigns with standard device requirements on this map.',
        );
        setSessionBusy(false);
        return;
      }
      if (!campaignIsActive) {
        setError('This campaign is not active; collection sessions cannot be started from the map.');
        setSessionBusy(false);
        return;
      }
      let fieldDeviceId: string | undefined;
      if (fieldDevicePolicy !== 'none') {
        if (!sessionDeviceGateOk || !effectiveFieldDeviceId) {
          setError('Select an assigned field device before starting (or ask an admin to assign one).');
          setSessionBusy(false);
          return;
        }
        fieldDeviceId = effectiveFieldDeviceId;
      }

      const registeredDevice =
        fieldDeviceId != null ? devices.find((d) => d.id === fieldDeviceId) : undefined;

      await startCampaignSession(campaignId, {
        start_lat: userLocation?.lat,
        start_lng: userLocation?.lng,
        start_location_accuracy_m:
          userLocation?.accuracyM != null && Number.isFinite(userLocation.accuracyM)
            ? userLocation.accuracyM
            : undefined,
        device_info: userLocation
          ? {
            source: 'public_map',
            focused_zone_id: focusZoneId,
            approx_location: { lat: userLocation.lat, lng: userLocation.lng },
            approx_location_accuracy_m:
              userLocation.accuracyM != null && Number.isFinite(userLocation.accuracyM)
                ? userLocation.accuracyM
                : undefined,
          }
          : { source: 'public_map', focused_zone_id: focusZoneId },
        field_device_id: fieldDeviceId,
        position_source: POSITION_SOURCE_BROWSER_GEOLOCATION,
        collection_device: buildSessionCollectionDevice({
          fieldDeviceId,
          registeredDevice,
          userLocation,
        }),
      });
      const sess = await fetchCampaignSessions(campaignId);
      setSessions(sess);
      const activeRes = await refreshGlobalActiveSession();
      if (focusZoneId) {
        setCampaignWorkspaceResumeIdInSession(campaignId, focusZoneId);
      }
      restoreSessionZone(activeRes, focusZoneId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not start session';
      setError(msg);
      if (msg.toLowerCase().includes('active session')) {
        await refreshGlobalActiveSession();
      }
    } finally {
      setSessionBusy(false);
    }
  };

  const handleEndSession = async () => {
    if (!myActiveSession) return;
    setSessionBusy(true);
    setError(null);
    try {
      await endCampaignSession(activeSessionCampaignId, myActiveSession.id, {
        end_lat: userLocation?.lat,
        end_lng: userLocation?.lng,
      });
      const sess = await fetchCampaignSessions(campaignId);
      setSessions(sess);
      await refreshGlobalActiveSession();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not end session');
    } finally {
      setSessionBusy(false);
    }
  };

  const handleCampaignZoomIn = useCallback(() => {
    mapRef.current?.zoomIn();
  }, [mapRef]);

  const handleCampaignZoomOut = useCallback(() => {
    mapRef.current?.zoomOut();
  }, [mapRef]);

  const handleCampaignFitWorkspace = useCallback(() => {
    mapRef.current?.refitCampaignExtent();
  }, [mapRef]);

  const handleCampaignLocate = useCallback(() => {
    if (!navigator.geolocation) {
      onRequestLocationPrompt?.();
      return;
    }
    setIsLocating(true);
    requestCurrentPosition()
      .then((position) => {
        const location = mapPositionToCoordinates(position);
        const target = resolveFieldMappingLocateTarget(location);
        setUserLocation(target);
        mapRef.current?.flyTo({ lng: target.lng, lat: target.lat }, 15);
        setIsLocating(false);
      })
      .catch((error: GeolocationPositionError) => {
        console.error('Geolocation error:', error);
        setIsLocating(false);
        onRequestLocationPrompt?.();
      });
  }, [mapRef, onRequestLocationPrompt, setIsLocating, setUserLocation]);

  const compactMapToolClass =
    'group relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200/70 bg-gradient-to-b from-slate-50/[0.97] to-white text-slate-700 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.88)] transition-all duration-150 hover:border-slate-300/80 hover:from-slate-100/95 hover:to-slate-50/98 hover:text-slate-900 active:scale-[0.96] active:from-slate-100/90 active:to-slate-100/95 disabled:pointer-events-none disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-500/55 dark:border-gray-500/60 dark:from-gray-700/90 dark:to-gray-800/90 dark:text-gray-100 dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] dark:hover:border-gray-400/60 dark:hover:from-gray-600/90 dark:hover:to-gray-700/90 dark:hover:text-white [&_svg]:stroke-[1.85]';

  const compactMapToolClassMoreOpen =
    'border-cyan-500/40 bg-gradient-to-b from-cyan-50/95 to-cyan-100/40 text-cyan-950 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.75)] hover:border-cyan-500/50 hover:from-cyan-50 hover:to-cyan-100/55 hover:text-cyan-950 dark:border-cyan-400/35 dark:from-cyan-950/55 dark:to-gray-900/90 dark:text-cyan-100 dark:shadow-[inset_0_1px_0_0_rgba(34,211,238,0.12)] dark:hover:border-cyan-400/45 dark:hover:from-cyan-950/65 dark:hover:to-gray-900/95 dark:hover:text-cyan-50';

  const moreMenuItemClass =
    'flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-left text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100/95 dark:text-gray-100 dark:hover:bg-gray-700/80';

  const hardwareSectionRef = useRef<HTMLDivElement | null>(null);

  const requestEndSession = useCallback(() => {
    if (!myActiveSession || sessionBusy) return;
    setConfirmEndSessionOpen(true);
  }, [myActiveSession, sessionBusy]);

  const confirmEndSession = useCallback(() => {
    setConfirmEndSessionOpen(false);
    void handleEndSession();
  }, [handleEndSession]);

  const handleSessionToggle = () => {
    if (sessionBusy) return;
    if (myActiveSession) {
      requestEndSession();
      return;
    }
    if (!isAuthenticated) {
      onRequireAuth();
      return;
    }
    void (async () => {
      const activeRes = await refreshGlobalActiveSession();
      if (activeRes?.session && isSessionStatusActive(activeRes.session.status)) {
        restoreSessionZone(activeRes, focusZoneId);
        return;
      }
      void handleStartSession();
    })();
  };

  const handleFieldCapture = () => {
    if (!sessionAppliesToThisCampaign || !myActiveSession) {
      setError('Start a collection session before capturing field data.');
      return;
    }
    if (campaignStatus !== 'active') {
      setError('This campaign is paused — resume the campaign before capturing field data.');
      return;
    }
    if (liveGpsPaused) {
      setError('Resume Live GPS tracking before capturing field data.');
      return;
    }
    if (!userLocation) {
      onRequestLocationPrompt?.();
      setError('Waiting for a GPS fix — allow location access, then try Capture & label again.');
      return;
    }
    if (!onOpenContribution) return;
    onOpenContribution({ captureLiveLocation: true });
  };

  if (compactDock) {
    const geoReady = Boolean(userLocation);
    const hardwareReady = fieldDevicePolicy === 'none' ? true : sessionDeviceGateOk;
    const sessionToggleDisabled =
      sessionBusy ||
      (myActiveSession
        ? false
        : isAuthenticated &&
        (publicMapBlocksExternalGnssSessions || !canStartSessionOnPublicMap));

    const selectedSessionDevice =
      effectiveFieldDeviceId != null ? devices.find((d) => d.id === effectiveFieldDeviceId) : undefined;
    const hardwareNeedsAttention =
      isAuthenticated &&
      fieldDevicePolicy === 'assigned_device' &&
      sessionDevicePool.length > 0 &&
      !effectiveFieldDeviceId;

    let hardwarePillTitle: string;
    let hardwarePillSubtitle: string;
    if (fieldDevicePolicy === 'none') {
      hardwarePillTitle = 'Browser geolocation';
      hardwarePillSubtitle = geoReady ? 'Default · location fix ready' : 'Default · allow location when prompted';
    } else if (fieldDevicePolicy === 'external_gnss') {
      hardwarePillTitle = 'External GNSS required';
      hardwarePillSubtitle = 'Rover-backed — not on web map yet';
    } else if (sessionDevicePool.length === 0) {
      hardwarePillTitle = 'No field device assigned';
      hardwarePillSubtitle = 'Ask an admin or register in Console';
    } else if (selectedSessionDevice) {
      hardwarePillTitle = selectedSessionDevice.device_tag;
      hardwarePillSubtitle = devicePresence(selectedSessionDevice).label;
    } else {
      hardwarePillTitle = 'Choose field device';
      hardwarePillSubtitle = `${sessionDevicePool.length} assigned to you · tap to pick`;
    }

    const dockChipBase =
      'inline-flex h-9 items-center justify-center gap-1.5 rounded-xl border px-3 text-xs font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-45';
    const dockChipNeutral = clsx(
      dockChipBase,
      'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700',
    );
    const dockChipExpand = clsx(
      dockChipBase,
      'border-slate-200 bg-slate-50 text-slate-800 hover:bg-slate-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700',
    );
    const dockChipSessionStart = clsx(
      dockChipBase,
      'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-900/50',
    );
    const dockChipSessionStop = clsx(
      dockChipBase,
      'border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 dark:border-rose-800/50 dark:bg-rose-950/40 dark:text-rose-200 dark:hover:bg-rose-900/50',
    );
    const dockChipGlow = clsx(
      'ring-1 ring-offset-1 ring-offset-white dark:ring-offset-gray-900',
      liveConnected
        ? 'ring-emerald-400/40 dark:ring-emerald-500/30'
        : 'ring-slate-200/90 dark:ring-gray-500/40',
    );

    return (
      <div
        ref={campaignChromeRootRef}
        className={clsx(
          'fixed z-[55] flex flex-col gap-2 pointer-events-none',
          homeOverlayMode
            ? 'left-3 right-3 items-stretch md:left-auto md:right-4 md:w-[min(400px,calc(100vw-1.5rem))] md:items-end'
            : collapsed
              ? 'bottom-5 left-3 right-3 items-stretch md:left-auto md:right-4 md:w-[min(380px,calc(100vw-1.5rem))] md:items-end'
              : 'bottom-5 left-3 right-3 items-stretch max-md:inset-0 max-md:max-h-[100dvh] max-md:justify-end max-md:pb-[max(0.25rem,env(safe-area-inset-bottom))] max-md:pt-[env(safe-area-inset-top)] md:left-auto md:right-4 md:top-auto md:h-auto md:w-[min(380px,calc(100vw-1.5rem))] md:items-end md:max-h-none',
        )}
        style={
          homeOverlayMode
            ? { bottom: `max(${homeOverlayDockBottom}, env(safe-area-inset-bottom))` }
            : undefined
        }
      >
        <AnimatePresence mode="wait" initial={false}>
          {!collapsed && !homeOverlayMode ? (
            <motion.div
              key="campaign-expanded-stack"
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20, scale: 0.985 }}
              transition={{ type: 'spring', damping: 26, stiffness: 300 }}
              className="pointer-events-auto flex max-h-full min-h-0 w-full max-w-full flex-col gap-2 max-md:flex-1 md:items-end"
            >
              <div
                data-tour-target="campaign-details-card"
                className="relative flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-[1.25rem] border border-slate-200 bg-white shadow-xl dark:border-gray-600 dark:bg-gray-900 max-md:min-h-0 max-md:flex-1 max-md:overflow-y-auto max-md:rounded-b-none max-md:rounded-t-2xl max-md:border-x-0 max-md:border-b-0 md:max-h-[min(72vh,620px)] md:flex-none md:overflow-y-auto md:shadow-lg md:ring-1 md:ring-slate-200/60 md:backdrop-blur-sm dark:md:ring-gray-600/50"
              >
                <div
                  className="pointer-events-none absolute inset-0 hidden opacity-70 dark:opacity-90 md:block bg-[linear-gradient(135deg,rgba(6,182,212,0.07)_0%,rgba(16,185,129,0.05)_50%,transparent_78%)] dark:bg-[linear-gradient(135deg,rgba(6,182,212,0.11)_0%,rgba(16,185,129,0.07)_45%,transparent_75%)]"
                  aria-hidden
                />
                <div className="relative flex min-h-0 flex-1 flex-col md:min-h-0">
                  {!collapsed && isAuthenticated && authUser ? (
                    <button
                      type="button"
                      onClick={() => { if (onOpenProfile) onOpenProfile(); else onRequireAuth(); }}
                      className="flex w-full shrink-0 items-start gap-3 border-b border-slate-200/95 bg-gradient-to-b from-slate-50/95 to-white px-3 py-2.5 text-left transition-colors hover:from-slate-100 hover:to-slate-50 dark:border-gray-700 dark:from-gray-800/95 dark:to-gray-900 dark:hover:from-gray-700/95 dark:hover:to-gray-800">
                      <div
                        className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-slate-100 to-slate-200 text-[12px] font-bold text-slate-700 shadow-md ring-2 ring-white dark:from-gray-600 dark:to-gray-700 dark:text-gray-100 dark:ring-gray-700/80"
                        title="You — data in this session is tied to your signed-in account"
                      >
                        {authUser.avatar ? (
                          <img src={authUser.avatar} alt="" className="h-full w-full object-cover" />
                        ) : (
                          initialsFromDisplayName(authUser.name || authUser.email || '?')
                        )}
                      </div>
                      <div className="min-w-0 flex-1 pt-0.5">
                        <div className="truncate text-sm font-semibold leading-tight text-slate-900 dark:text-gray-50">
                          {authUser.name?.trim() || authUser.email?.split('@')[0] || 'You'}
                        </div>
                        {authUser.email ? (
                          <div className="mt-0.5 truncate text-[11px] leading-tight text-slate-500 dark:text-gray-400">
                            {authUser.email}
                          </div>
                        ) : null}
                        {viewerRoleBadgesList.length > 0 ? (
                          <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            {viewerRoleBadgesList.map((b) => (
                              <span
                                key={b.key}
                                className={clsx(
                                  'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold leading-none',
                                  b.className,
                                )}
                              >
                                {b.label}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="mt-1.5 text-[10px] font-medium text-slate-500 dark:text-gray-500">
                            Field workspace
                          </div>
                        )}
                      </div>
                    </button>
                  ) : null}
                  {!collapsed && (onExitCampaign || onBrowseFieldCampaigns || onGoHome) ? (
                    <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200/90 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
                      <div className="flex min-w-0 items-center gap-1.5">
                        {onGoHome ? (
                          <button
                            type="button"
                            onClick={() => onGoHome()}
                            className="inline-flex items-center gap-1 rounded-lg border border-cyan-200 bg-cyan-50 px-2.5 py-1.5 text-[11px] font-semibold text-cyan-900 transition hover:bg-cyan-100 dark:border-cyan-800/60 dark:bg-cyan-950/50 dark:text-cyan-100 dark:hover:bg-cyan-900/50"
                            title="Explore map while session stays active"
                          >
                            <Home className="h-3.5 w-3.5" />
                            Home
                          </button>
                        ) : null}
                        {onExitCampaign ? (
                          <button
                            type="button"
                            onClick={requestExitCampaign}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                            title="Leave campaign"
                          >
                            <ArrowLeft className="h-3.5 w-3.5" />
                            Exit
                          </button>
                        ) : null}
                      </div>
                      {onBrowseFieldCampaigns ? (
                        <button
                          type="button"
                          onClick={() => onBrowseFieldCampaigns()}
                          className="shrink-0 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                          title="Switch campaign"
                        >
                          Switch campaign
                        </button>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="relative shrink-0 border-b border-slate-200 bg-white px-3 sm:px-3.5 py-2.5 dark:border-gray-700 dark:bg-gray-900">
                    <div className="relative flex items-start gap-3 min-w-0">
                      <div className="flex h-11 w-11 shrink-0 overflow-hidden rounded-2xl shadow-md shadow-cyan-900/15 ring-1 ring-white/25 dark:ring-white/10">
                        {organizationLogoUrl && !orgLogoLoadFailed ? (
                          <img
                            src={organizationLogoUrl}
                            alt=""
                            className="h-full w-full bg-white object-contain dark:bg-gray-900"
                            onError={() => setOrgLogoLoadFailed(true)}
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-cyan-500 to-emerald-600">
                            <Layers className="h-5 w-5 text-white" strokeWidth={2} />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1 pt-0.5">
                        <div className="mb-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-cyan-700 dark:text-cyan-300/90">
                            Field campaign
                          </p>
                          {campaignDescription ? (
                            <CompactInfoPopover
                              panelId="campaign-desc"
                              activePanel={detailInfoPanel}
                              setActivePanel={setDetailInfoPanel}
                              label="Campaign description and goal"
                            >
                              <p className="font-semibold text-slate-900 dark:text-gray-100">Goal & description</p>
                              <p className="mt-2 whitespace-pre-wrap text-slate-700 dark:text-gray-300">
                                {campaignDescription}
                              </p>
                            </CompactInfoPopover>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                          <h2 className="min-w-0 flex-1 basis-[10rem] text-[15px] font-semibold leading-snug tracking-tight text-slate-900 dark:text-white">
                            {campaignName || 'Campaign workspace'}
                          </h2>
                          <div className="flex shrink-0 flex-nowrap items-center gap-2">
                            <span
                              className={clsx(
                                'inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold',
                                geoReady
                                  ? 'border-emerald-200/90 bg-emerald-50/95 text-emerald-900 dark:border-emerald-700/45 dark:bg-emerald-950/45 dark:text-emerald-200'
                                  : 'border-amber-200/90 bg-amber-50/95 text-amber-950 dark:border-amber-700/45 dark:bg-amber-950/35 dark:text-amber-200',
                              )}
                            >
                              {geoReady ? 'Phone location on' : 'Location needed'}
                            </span>
                            <span
                              className={clsx(
                                'inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold',
                                fieldDevicePolicy === 'none'
                                  ? 'border-slate-200/90 bg-white/80 text-slate-700 dark:border-gray-600/55 dark:bg-gray-800/90 dark:text-gray-200'
                                  : hardwareReady
                                    ? 'border-emerald-200/90 bg-emerald-50/95 text-emerald-900 dark:border-emerald-700/45 dark:bg-emerald-950/45 dark:text-emerald-200'
                                    : 'border-amber-200/90 bg-amber-50/95 text-amber-950 dark:border-amber-700/45 dark:bg-amber-950/35 dark:text-amber-200',
                              )}
                            >
                              {fieldDevicePolicy === 'none'
                                ? 'Hardware optional'
                                : hardwareReady
                                  ? 'Hardware ready'
                                  : 'Hardware required'}
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[11px] text-slate-600 dark:text-gray-400">
                          {districtName && (
                            <span className="inline-flex items-center gap-1 text-slate-700 dark:text-gray-300">
                              <MapPin className="h-3.5 w-3.5 shrink-0 text-cyan-600 dark:text-cyan-400" />
                              {districtName}
                            </span>
                          )}
                          {campaignStatus && (
                            <span className="rounded-md border border-slate-200/90 bg-white/70 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-slate-600 dark:border-gray-600/55 dark:bg-gray-800/80 dark:text-gray-300">
                              {campaignStatus}
                            </span>
                          )}
                          {liveConnected && isAuthenticated && (
                            <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                              <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                              </span>
                              Live
                            </span>
                          )}
                        </div>
                        {campaignDescription ? (
                          <div
                            className="mt-2 flex items-start gap-1 border-t border-slate-100 pt-2 text-[11px] leading-relaxed text-slate-600 dark:border-gray-700/80 dark:text-gray-400"
                            title={campaignDescription}
                          >
                            <BookOpen className="mt-[2px] h-3.5 w-3.5 shrink-0 text-cyan-600 dark:text-cyan-400" aria-hidden />
                            <p className="min-w-0 flex-1 line-clamp-3">{campaignDescription}</p>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 px-2.5 py-2 text-slate-800 dark:text-gray-100">
                    {error && (
                      <div className="flex gap-2 rounded-xl bg-red-500/10 border border-red-500/25 px-3 py-2 text-xs text-red-800 dark:text-red-200">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{error}</span>
                      </div>
                    )}
                    {activeSessionOnOtherCampaign ? (
                      <div className="flex gap-2 rounded-xl border border-amber-300/80 bg-amber-50/95 px-3 py-2 text-xs text-amber-950 dark:border-amber-500/35 dark:bg-amber-500/12 dark:text-amber-100">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>
                          You have an active session
                          {globalActiveCampaignName ? (
                            <>
                              {' '}
                              on <strong>{globalActiveCampaignName}</strong>
                            </>
                          ) : (
                            ' on another campaign'
                          )}
                          . Use <strong>Stop</strong> below to end it before starting a new session here.
                        </span>
                      </div>
                    ) : null}

                    {!isAuthenticated ? (
                      <div className="rounded-2xl border border-slate-300/70 dark:border-gray-600/50 bg-white dark:bg-gray-900/60 p-4 space-y-3 shadow-md">
                        <div className="flex items-start gap-2.5">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-cyan-500/15 text-cyan-700 dark:text-cyan-300">
                            <MapPinned className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-900 dark:text-gray-100">
                              Sign in for the full campaign workspace
                            </p>
                            <p className="text-[11px] text-slate-600 dark:text-gray-400 mt-1 leading-relaxed">
                              Overlays, sessions, and team sync load once you’re signed in with campaign access.
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={onRequireAuth}
                            className="inline-flex items-center justify-center rounded-xl bg-gradient-to-b from-violet-600 to-violet-700 px-3 py-2 text-xs font-semibold text-white shadow-md border border-violet-500/30"
                          >
                            Sign in
                          </button>
                          {onOpenSettings ? (
                            <button
                              type="button"
                              onClick={() => onOpenSettings()}
                              className="inline-flex items-center justify-center rounded-xl border border-slate-300/90 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700/80"
                            >
                              Map settings
                            </button>
                          ) : null}
                        </div>
                        <p className="text-[10px] text-slate-500 dark:text-gray-400 leading-relaxed border-t border-slate-200/80 dark:border-gray-700/50 pt-3">
                          After signing in on the main map: open <strong>Profile</strong> →{' '}
                          <strong>Field campaigns</strong>, or <strong>Settings</strong> →{' '}
                          <strong>Field campaigns</strong>, to open your assignments. Use the back control below to leave
                          this link without signing in.
                        </p>
                        {onExitCampaign ? (
                          <button
                            type="button"
                            onClick={requestExitCampaign}
                            className="w-full flex items-center justify-center gap-2 rounded-xl border border-slate-300/80 dark:border-gray-600/60 bg-slate-50 dark:bg-gray-800/50 py-2.5 text-xs font-semibold text-slate-800 dark:text-gray-100 hover:bg-slate-100 dark:hover:bg-gray-800"
                          >
                            <ArrowLeft className="w-4 h-4" />
                            Back to explore map
                          </button>
                        ) : null}
                      </div>
                    ) : (
                      <>
                        <section className="rounded-xl border border-slate-200 bg-slate-50 p-2 dark:border-gray-600 dark:bg-gray-800/95">
                          <div className="mb-2 flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => setMapOverlaysExpanded((v) => !v)}
                              aria-expanded={mapOverlaysExpanded}
                              className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-lg border border-transparent px-1 py-0.5 text-left transition hover:bg-slate-200/60 dark:hover:bg-gray-700/50"
                            >
                              <div className="flex min-w-0 flex-col gap-0.5">
                                <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-600 dark:text-gray-300">
                                  Map overlays
                                </span>
                                {!mapOverlaysExpanded ? (
                                  <span className="text-[9px] font-medium capitalize text-slate-500 dark:text-gray-400">
                                    {mapOverlaysSummary}
                                  </span>
                                ) : null}
                              </div>
                              <ChevronDown
                                className={clsx(
                                  'h-4 w-4 shrink-0 text-slate-500 transition dark:text-gray-400',
                                  mapOverlaysExpanded && '-rotate-180',
                                )}
                                strokeWidth={2.25}
                                aria-hidden
                              />
                            </button>
                            <CompactInfoPopover
                              panelId="overlays"
                              activePanel={detailInfoPanel}
                              setActivePanel={setDetailInfoPanel}
                              label="About map overlays"
                            >
                              <p className="font-semibold text-slate-900 dark:text-gray-100">What these toggles do</p>
                              <ul className="mt-1.5 list-inside list-disc space-y-1 text-slate-600 dark:text-gray-300">
                                <li>
                                  <strong className="text-slate-800 dark:text-gray-100">Collection zones</strong> — polygon
                                  outlines for this campaign (when zone data is loaded).
                                </li>
                                <li>
                                  With a <strong className="text-slate-800 dark:text-gray-100">zone selected</strong>, use{' '}
                                  <strong className="text-slate-800 dark:text-gray-100">All zones</strong> /{' '}
                                  <strong className="text-slate-800 dark:text-gray-100">This zone only</strong> to show every
                                  boundary or focus on the selected one; switch zones from the list in{' '}
                                  <strong className="text-slate-800 dark:text-gray-100">Zone view</strong> if outlines are
                                  hidden.
                                </li>
                                <li>
                                  <strong className="text-slate-800 dark:text-gray-100">Synced captures</strong> — team
                                  submission points on the map when capture geometry exists.
                                </li>
                              </ul>
                              <p className="mt-2 text-slate-500 dark:text-gray-400">
                                Both default to <strong className="text-slate-700 dark:text-gray-200">on</strong> when data
                                is available. Turn them off to reduce clutter.
                              </p>
                            </CompactInfoPopover>
                          </div>
                          {mapOverlaysExpanded ? (
                            <>
                              <p className="mb-2 text-[9px] font-medium text-slate-500 dark:text-gray-400">Defaults on when data is available.</p>
                              <div className="space-y-1.5">
                                <OverlayToggleRow
                                  title="Collection zones"
                                  enabled={showZones}
                                  disabled={!zonesFc}
                                  onToggle={() => setShowZones((v) => !v)}
                                />
                                {showZones && focusZoneId && zonesFc?.features?.length ? (
                                  <div
                                    className="rounded-xl border border-slate-200/90 bg-slate-100/60 p-0.5 dark:border-gray-600/55 dark:bg-gray-900/50"
                                    role="group"
                                    aria-label="Zone boundary scope"
                                  >
                                    <div className="grid grid-cols-2 gap-0.5">
                                      <button
                                        type="button"
                                        onClick={() => setZoneBoundaryScope('all')}
                                        className={clsx(
                                          'rounded-[10px] px-2 py-1.5 text-[10px] font-semibold transition',
                                          zoneBoundaryScope === 'all'
                                            ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/90 dark:bg-gray-800 dark:text-gray-50 dark:ring-gray-600/80'
                                            : 'text-slate-500 hover:text-slate-800 dark:text-gray-400 dark:hover:text-gray-200',
                                        )}
                                      >
                                        All zones
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setZoneBoundaryScope('selected')}
                                        className={clsx(
                                          'rounded-[10px] px-2 py-1.5 text-[10px] font-semibold transition',
                                          zoneBoundaryScope === 'selected'
                                            ? 'bg-white text-slate-900 shadow-sm ring-1 ring-cyan-500/35 dark:bg-gray-800 dark:text-cyan-100 dark:ring-cyan-400/40'
                                            : 'text-slate-500 hover:text-slate-800 dark:text-gray-400 dark:hover:text-gray-200',
                                        )}
                                      >
                                        This zone only
                                      </button>
                                    </div>
                                  </div>
                                ) : null}
                                <OverlayToggleRow
                                  title="Synced captures"
                                  enabled={showLive}
                                  disabled={!liveFc}
                                  onToggle={() => setShowLive((v) => !v)}
                                />
                              </div>
                              <div className="mt-2 border-t border-slate-200 pt-2 dark:border-gray-600/80">
                                <div className="mb-1.5 flex items-center gap-1">
                                  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-600 dark:text-gray-300">
                                    Point colors
                                  </span>
                                  <CompactInfoPopover
                                    panelId="points"
                                    activePanel={detailInfoPanel}
                                    setActivePanel={setDetailInfoPanel}
                                    label="About capture point colors and sync"
                                  >
                                    <p>
                                      Capture markers use QA status colors on the map: pending (amber), approved (green),
                                      rejected (red).
                                    </p>
                                    <p className="mt-2">
                                      When <strong className="text-slate-900 dark:text-gray-100">live</strong> is connected,
                                      updates stream in. When idle, refreshes use{' '}
                                      <strong className="font-mono text-slate-800 dark:text-gray-100">incremental sync</strong>{' '}
                                      with a cursor (
                                      <code className="rounded bg-slate-100 px-1 text-[10px] dark:bg-gray-800">since=</code>
                                      ) so only new changes load.
                                    </p>
                                  </CompactInfoPopover>
                                </div>
                                <div className="flex flex-wrap gap-x-3 gap-y-1">
                                  {CAPTURE_STATUS_LEGEND.map((item) => (
                                    <span
                                      key={item.key}
                                      className="inline-flex items-center gap-1.5 text-[10px] font-medium text-slate-700 dark:text-gray-200"
                                    >
                                      <span
                                        className="h-2.5 w-2.5 rounded-full shadow ring-1 ring-black/15 dark:ring-white/25"
                                        style={{ backgroundColor: item.color }}
                                        aria-hidden
                                      />
                                      {item.label}
                                    </span>
                                  ))}
                                </div>
                                <p className="mt-1.5 text-[10px] font-medium text-slate-600 dark:text-gray-300">
                                  {liveConnected ? (
                                    <span className="text-emerald-700 dark:text-emerald-400">Live connected</span>
                                  ) : (
                                    <span>Idle — use the info icon for sync details</span>
                                  )}
                                </p>
                              </div>
                            </>
                          ) : null}
                        </section>

                        <section className="rounded-xl border border-slate-200 bg-slate-50 p-2 dark:border-gray-600 dark:bg-gray-800/95">
                          <div className="mb-1.5 flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => setZoneSectionExpanded((v) => !v)}
                              aria-expanded={zoneSectionExpanded}
                              className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-lg border border-transparent px-1 py-0.5 text-left transition hover:bg-slate-200/60 dark:hover:bg-gray-700/50"
                            >
                              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-600 dark:text-gray-300">
                                  Zone view
                                </span>
                                {!zoneSectionExpanded && focusedZoneMeta ? (
                                  <span className="truncate text-[10px] font-semibold text-slate-700 dark:text-gray-200">
                                    {focusedZoneMeta.name}
                                  </span>
                                ) : !zoneSectionExpanded && !focusZoneId ? (
                                  <span className="text-[10px] font-medium text-slate-500 dark:text-gray-400">No zone — expand to list</span>
                                ) : null}
                              </div>
                              <ChevronDown
                                className={clsx(
                                  'h-4 w-4 shrink-0 text-slate-500 transition dark:text-gray-400',
                                  zoneSectionExpanded && '-rotate-180',
                                )}
                                strokeWidth={2.25}
                                aria-hidden
                              />
                            </button>
                            <CompactInfoPopover
                              panelId="zone"
                              activePanel={detailInfoPanel}
                              setActivePanel={setDetailInfoPanel}
                              label="About zone selection"
                            >
                              <p>
                                Tap a <strong className="text-slate-900 dark:text-gray-100">collection zone</strong> on the
                                map or <strong className="text-slate-900 dark:text-gray-100">pick from the list</strong>. You
                                can also open a console link with{' '}
                                <code className="rounded bg-slate-100 px-1 text-[10px] dark:bg-gray-800">zone_id</code>.
                              </p>
                              <p className="mt-2">
                                <strong className="text-slate-900 dark:text-gray-100">During an active session</strong>, the
                                zone is fixed. Use <strong className="text-slate-900 dark:text-gray-100">Stop</strong> in the
                                dock first, then you can switch zones.
                              </p>
                              <p className="mt-2">
                                Tap the <strong className="text-slate-900 dark:text-gray-100">selected zone</strong> card to
                                pan and zoom the map to that zone again. The map briefly highlights the zone outline so you
                                can see it clearly.
                              </p>
                            </CompactInfoPopover>
                          </div>
                          {zoneSelectionLocked ? (
                            <p className="mt-1 rounded-lg border border-amber-200/90 bg-amber-50/95 px-2.5 py-1.5 text-[10px] font-medium leading-snug text-amber-950 dark:border-amber-500/35 dark:bg-amber-500/12 dark:text-amber-100/95">
                              Session in progress — end the session to change zones or clear the selection.
                            </p>
                          ) : null}
                          {focusZoneId && focusedZoneMeta ? (
                            <div
                              className={clsx(
                                'mt-2 flex min-w-0 items-center gap-1 rounded-2xl border border-slate-200/95 bg-white py-1 pl-1 pr-1 shadow-sm ring-1 ring-black/[0.03] transition-shadow duration-500 dark:border-gray-600/80 dark:bg-gray-900/95 dark:ring-white/[0.06]',
                                zoneFlyIndicatorPulse &&
                                'ring-2 ring-cyan-400/85 ring-offset-2 ring-offset-white shadow-[0_0_22px_-4px_rgba(34,211,238,0.45)] dark:ring-cyan-400/65 dark:ring-offset-gray-900',
                              )}
                            >
                              <button
                                type="button"
                                onClick={flyToSelectedZoneOnMap}
                                disabled={!zoneFocusGeometry}
                                title={
                                  zoneFocusGeometry
                                    ? 'Show this zone on the map'
                                    : 'Zone boundary still loading'
                                }
                                className={clsx(
                                  'flex min-w-0 flex-1 items-center gap-2.5 rounded-xl px-2 py-2 text-left transition outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/50 focus-visible:ring-offset-1 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900',
                                  zoneFocusGeometry
                                    ? 'hover:bg-slate-50/95 active:bg-slate-100/90 dark:hover:bg-gray-800/75 dark:active:bg-gray-800'
                                    : 'cursor-not-allowed opacity-60',
                                )}
                              >
                                <div
                                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl shadow-inner ring-1 ring-black/10 dark:ring-white/15"
                                  style={{
                                    background: focusedZoneMeta.color
                                      ? `linear-gradient(145deg, ${focusedZoneMeta.color}33 0%, ${focusedZoneMeta.color}18 100%)`
                                      : undefined,
                                  }}
                                >
                                  {focusedZoneMeta.color ? (
                                    <span
                                      className="h-4 w-4 rounded-full border-2 border-white/90 shadow-sm dark:border-gray-900/60"
                                      style={{ backgroundColor: focusedZoneMeta.color }}
                                      aria-hidden
                                    />
                                  ) : (
                                    <MapPinned className="h-5 w-5 text-slate-500 dark:text-gray-400" strokeWidth={2} />
                                  )}
                                </div>
                                <div className="min-w-0 flex-1 py-0.5">
                                  <div className="text-[9px] font-bold uppercase tracking-[0.12em] text-cyan-700 dark:text-cyan-300/90">
                                    Selected zone
                                  </div>
                                  <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                                    {focusedZoneMeta.name}
                                  </div>
                                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                    {focusedZoneMeta.status ? (
                                      <span className="inline-flex rounded-full border border-slate-200/90 bg-slate-50 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-slate-600 dark:border-gray-600 dark:bg-gray-800/90 dark:text-gray-300">
                                        {focusedZoneMeta.status}
                                      </span>
                                    ) : null}
                                    {focusedZoneMeta.progressPct != null &&
                                      Number.isFinite(focusedZoneMeta.progressPct) ? (
                                      <span className="text-[10px] font-medium text-slate-600 dark:text-gray-400">
                                        {Math.round(focusedZoneMeta.progressPct)}% done
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              </button>
                              {onClearFocusedZone && !zoneSelectionLocked ? (
                                <button
                                  type="button"
                                  onClick={onClearFocusedZone}
                                  title="Clear zone"
                                  aria-label="Clear zone selection"
                                  className="flex h-9 w-9 shrink-0 self-center items-center justify-center rounded-full border border-slate-200/90 bg-slate-50 text-slate-600 shadow-sm transition hover:bg-slate-100 hover:text-slate-900 dark:border-gray-600 dark:bg-gray-800/95 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
                                >
                                  <X className="h-4 w-4" strokeWidth={2.25} />
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                          {!zoneSelectionLocked && isAuthenticated && viewerMayClaim && focusZoneId && focusedZoneMeta ? (
                            <div className="mt-2 rounded-xl border border-slate-200/90 bg-slate-50/80 px-2.5 py-2 dark:border-gray-600/70 dark:bg-gray-800/60">
                              {focusedZoneMeta.assignedToId != null && focusedZoneMeta.assignedToId === myUserId ? (
                                <p className="text-[10px] font-medium leading-snug text-emerald-800 dark:text-emerald-200/90">
                                  This zone is assigned to you — it will show on the campaign Zones tab in the developer
                                  console.
                                </p>
                              ) : focusedZoneMeta.assignedToId != null ? (
                                <p className="text-[10px] font-medium leading-snug text-slate-600 dark:text-gray-400">
                                  Assigned to{' '}
                                  <span className="text-slate-900 dark:text-gray-200">
                                    {focusedZoneMeta.assignedToName || 'a teammate'}
                                  </span>
                                  . A supervisor can reassign it in the console.
                                </p>
                              ) : !focusedZoneClaimEligible ? (
                                <p className="text-[10px] font-medium leading-snug text-amber-900 dark:text-amber-200/90">
                                  This zone cannot be claimed from the map (completed or needs review). Ask a supervisor to
                                  reassign it in the console.
                                </p>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => void handleClaimZone()}
                                  disabled={claimZoneBusy || loading}
                                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-cyan-500/40 bg-gradient-to-b from-cyan-500/15 to-teal-500/10 px-3 py-2 text-[11px] font-semibold text-cyan-950 shadow-sm transition hover:from-cyan-500/25 disabled:cursor-not-allowed disabled:opacity-50 dark:border-cyan-400/35 dark:from-cyan-400/18 dark:to-teal-900/25 dark:text-cyan-50"
                                >
                                  <UserPlus className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
                                  {claimZoneBusy ? 'Claiming…' : 'Claim this zone'}
                                </button>
                              )}
                            </div>
                          ) : null}
                          {!focusZoneId ? (
                            zoneSectionExpanded ? (
                              <p className="mt-2 text-xs font-medium text-slate-700 dark:text-gray-200">
                                No zone selected — tap the map or choose from the list below.
                              </p>
                            ) : null
                          ) : !zonesFc?.features?.length && loading ? (
                            <p className="mt-2 text-[11px] text-slate-500">Loading zone…</p>
                          ) : focusZoneId && !focusedZoneMeta && !loading ? (
                            <p className="mt-2 text-[11px] text-amber-800 dark:text-amber-200/90">
                              Zone not found in this campaign.
                            </p>
                          ) : null}
                          {zoneSectionExpanded && onSelectCampaignZone && zoneListItems.length > 0 ? (
                            <div
                              className={clsx(
                                'mt-2 max-h-32 space-y-1 overflow-y-auto rounded-lg border border-slate-200/80 bg-white/60 p-1 dark:border-gray-600/70 dark:bg-gray-900/50',
                                zoneSelectionLocked && 'opacity-50',
                              )}
                            >
                              {isAuthenticated && myZoneItemsInCampaign.length > 0 ? (
                                <p className="px-2 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-cyan-800 dark:text-cyan-200/90">
                                  Your zones ({myZoneItemsInCampaign.length}) — tap to select
                                </p>
                              ) : null}
                              {zoneListItems.map((z) => {
                                const isMine = myUserId != null && z.assignedToId === myUserId;
                                const blockedByRole = isCollectorOnly && !isMine;
                                const isDisabled = zoneSelectionLocked || blockedByRole;
                                return (
                                  <button
                                    key={z.id}
                                    type="button"
                                    disabled={isDisabled}
                                    title={zoneSelectionLocked ? 'Stop session to change zone' : blockedByRole ? 'This zone is not assigned to you' : undefined}
                                    onClick={() => onSelectCampaignZone(z.id)}
                                    className={clsx(
                                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[11px] font-medium transition disabled:cursor-not-allowed',
                                      focusZoneId === z.id
                                        ? 'bg-cyan-500/15 text-cyan-950 ring-1 ring-cyan-500/35 dark:bg-cyan-400/12 dark:text-cyan-50 dark:ring-cyan-400/30'
                                        : blockedByRole
                                          ? 'text-slate-400 dark:text-gray-500'
                                          : 'text-slate-800 hover:bg-slate-100/90 dark:text-gray-100 dark:hover:bg-gray-800/80',
                                    )}
                                  >
                                    <span
                                      className={clsx('h-2 w-2 shrink-0 rounded-full border border-slate-300/80 dark:border-gray-600', blockedByRole && 'opacity-40')}
                                      style={{ backgroundColor: zoneStatusColor(z.status) }}
                                      aria-hidden
                                    />
                                    <span className="min-w-0 flex-1 truncate">{z.name}</span>
                                    {isMine ? (
                                      <span className="shrink-0 rounded-md bg-cyan-500/15 px-1 py-px text-[8px] font-bold uppercase tracking-wide text-cyan-900 dark:bg-cyan-400/20 dark:text-cyan-100">
                                        Yours
                                      </span>
                                    ) : null}
                                    {z.status ? (
                                      <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide opacity-70">
                                        {z.status}
                                      </span>
                                    ) : null}
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}
                          {zoneSectionExpanded && zoneListItems.length > 0 ? (
                            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-500 dark:text-gray-400">
                              <span className="font-medium">Map colors by status:</span>
                              <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-[#94a3b8]" />Unassigned</span>
                              <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-[#3b82f6]" />Assigned</span>
                              <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-[#f59e0b]" />In progress</span>
                              <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-[#22c55e]" />Completed</span>
                              <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-[#8b5cf6]" />Needs review</span>
                            </div>
                          ) : null}
                        </section>

                        <section className="rounded-xl border border-slate-200 bg-slate-50 p-2 dark:border-gray-600 dark:bg-gray-800/95">
                          <div className="mb-1.5 flex items-start justify-between gap-2">
                            <div className="flex min-w-0 flex-1 items-start gap-2">
                              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-500/15 ring-1 ring-violet-500/25 dark:bg-violet-400/10 dark:ring-violet-400/20">
                                <Radio className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-1">
                                  <span className="text-xs font-semibold text-slate-900 dark:text-gray-50">Session</span>
                                  <CompactInfoPopover
                                    panelId="session"
                                    activePanel={detailInfoPanel}
                                    setActivePanel={setDetailInfoPanel}
                                    label="About sessions"
                                  >
                                    <p>
                                      Use <strong className="text-slate-900 dark:text-gray-100">Start</strong> in the dock to begin collecting — you can optionally select a zone first to focus your work area. While active, your position and captures sync with the team when
                                      permitted.
                                    </p>
                                    <p className="mt-2 text-slate-600 dark:text-gray-300">
                                      Field source (GPS / device) is configured in <strong>Field source</strong> below.
                                    </p>
                                  </CompactInfoPopover>
                                </div>
                                {!sessionPausedBodyHidden ? (
                                  <>
                                    <p className="mt-0.5 text-[10px] text-slate-600 dark:text-gray-400">
                                      {myActiveSession
                                        ? 'Session is active — tap it below to restore your zone.'
                                        : 'Select a zone, then Start.'}
                                    </p>
                                    <button
                                      type="button"
                                      onClick={() => setSessionSyncInstructionOpen((v) => !v)}
                                      aria-expanded={sessionSyncInstructionOpen}
                                      className="mt-2 flex w-full items-center gap-2 rounded-lg border border-slate-200/85 bg-slate-50/95 px-2.5 py-1.5 text-left transition hover:bg-slate-100/90 dark:border-gray-600/55 dark:bg-gray-800/55 dark:hover:bg-gray-800/80"
                                    >
                                      <span className="min-w-0 flex-1 text-[10px] font-semibold text-slate-700 dark:text-gray-300">
                                        Live updates &amp; incremental sync
                                      </span>
                                      <ChevronDown
                                        className={clsx(
                                          'h-4 w-4 shrink-0 text-slate-500 transition dark:text-gray-400',
                                          sessionSyncInstructionOpen && '-rotate-180',
                                        )}
                                        strokeWidth={2.25}
                                        aria-hidden
                                      />
                                    </button>
                                    {sessionSyncInstructionOpen ? (
                                      <div className="mt-1.5 rounded-lg border border-slate-200/85 bg-slate-50/95 px-2.5 py-2 dark:border-gray-600/55 dark:bg-gray-800/55">
                                        <p className="text-[10px] leading-snug text-slate-600 dark:text-gray-400">
                                          When <strong className="text-slate-800 dark:text-gray-200">live</strong> is
                                          connected, team capture updates stream in on the map. When idle, refreshes use{' '}
                                          <strong className="text-slate-800 dark:text-gray-200">incremental sync</strong> with
                                          a cursor (
                                          <code className="rounded bg-slate-100 px-0.5 text-[9px] dark:bg-gray-900">since=</code>
                                          ) so only new changes load.
                                        </p>
                                        <a
                                          href={FIELD_COLLECTION_DOCS_SYNC_URL}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="mt-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-cyan-700 hover:text-cyan-900 hover:underline dark:text-cyan-400 dark:hover:text-cyan-300"
                                        >
                                          Learn more
                                          <BookOpen className="h-3 w-3 opacity-80" strokeWidth={2} />
                                        </a>
                                      </div>
                                    ) : null}
                                  </>
                                ) : null}
                              </div>
                            </div>
                            <div className="flex shrink-0 items-start gap-1">
                              {showPausedCampaignSessionGate ? (
                                <button
                                  type="button"
                                  onClick={() => setSessionPausedDetailExpanded((v) => !v)}
                                  aria-expanded={sessionPausedDetailExpanded}
                                  title={
                                    sessionPausedDetailExpanded
                                      ? 'Hide session details'
                                      : 'Show session details'
                                  }
                                  className="inline-flex shrink-0 items-center justify-center rounded-lg border border-slate-200/90 bg-white/90 p-1.5 text-slate-700 shadow-sm hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700/80"
                                >
                                  <ChevronDown
                                    className={clsx(
                                      'h-4 w-4 transition text-slate-500 dark:text-gray-400',
                                      sessionPausedDetailExpanded && '-rotate-180',
                                    )}
                                    strokeWidth={2.25}
                                    aria-hidden
                                  />
                                </button>
                              ) : null}
                              {onOpenSettings ? (
                                <button
                                  type="button"
                                  onClick={() => onOpenSettings()}
                                  title="Settings"
                                  aria-label="Settings"
                                  className="inline-flex shrink-0 items-center justify-center rounded-lg border border-slate-200/90 bg-white/90 p-1.5 text-slate-700 shadow-sm hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700/80"
                                >
                                  <Settings className="h-4 w-4" strokeWidth={2} />
                                </button>
                              ) : null}
                            </div>
                          </div>
                          {showPausedCampaignSessionGate ? (
                            <p className="text-[11px] text-amber-800 dark:text-amber-200/90 mb-2 rounded-lg border border-amber-200/80 bg-amber-50/90 px-2.5 py-2 dark:border-amber-500/35 dark:bg-amber-500/10">
                              This campaign is not <strong className="font-semibold">active</strong>. Sessions cannot be
                              started until it is active.
                            </p>
                          ) : null}
                          {!sessionPausedBodyHidden ? (
                            <>
                              {myActiveSession ? (
                                <div className="space-y-2">
                                  <button
                                    type="button"
                                    onClick={() => void handleActivateActiveSession()}
                                    className="w-full rounded-xl border border-emerald-200/90 bg-emerald-50/80 px-3 py-2.5 text-left transition hover:bg-emerald-100/90 dark:border-emerald-800/50 dark:bg-emerald-950/35 dark:hover:bg-emerald-900/45"
                                    title="Restore the zone this session was started in"
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="text-[11px] font-semibold text-emerald-900 dark:text-emerald-100">
                                        Your active session
                                      </span>
                                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200">
                                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" aria-hidden />
                                        Live
                                      </span>
                                    </div>
                                    <p className="mt-1 text-[11px] text-emerald-800/90 dark:text-emerald-200/90">
                                      Active since{' '}
                                      {new Date(myActiveSession.started_at).toLocaleString(undefined, {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        month: 'short',
                                        day: 'numeric',
                                      })}
                                    </p>
                                    {myActiveSession.focused_zone_id ? (
                                      <p className="mt-1 text-[10px] text-emerald-700/90 dark:text-emerald-300/90">
                                        Tap to select session zone
                                      </p>
                                    ) : null}
                                    {myActiveSession.position_source ? (
                                      <p className="mt-1 text-[10px] text-emerald-700/80 dark:text-emerald-300/80">
                                        Source:{' '}
                                        <span className="font-mono">{myActiveSession.position_source}</span>
                                      </p>
                                    ) : null}
                                  </button>
                                  <div className="rounded-xl border border-slate-200/90 bg-white/85 px-3 py-2 dark:border-gray-600/60 dark:bg-gray-900/45 space-y-2">
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="text-[11px] font-medium text-slate-800 dark:text-gray-200">Live GPS</span>
                                      <span
                                        className={clsx(
                                          'text-[10px] font-semibold uppercase',
                                          liveGpsPaused
                                            ? 'text-amber-700 dark:text-amber-400'
                                            : 'text-emerald-700 dark:text-emerald-400',
                                        )}
                                      >
                                        {liveGpsPaused ? 'Paused' : 'Tracking'}
                                      </span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => setLiveGpsPaused((p) => !p)}
                                      className="w-full flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white text-slate-800 text-[11px] font-medium py-2 hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700/80"
                                    >
                                      {liveGpsPaused ? (
                                        <>
                                          <Play className="w-3.5 h-3.5" />
                                          Resume
                                        </>
                                      ) : (
                                        <>
                                          <Pause className="w-3.5 h-3.5" />
                                          Pause
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  {fieldDevicePolicy !== 'none' && sessionDevicePool.length === 0 && (
                                    <p className="text-[11px] text-amber-800 dark:text-amber-400/90 leading-snug mb-2">
                                      {fieldDevicePolicy === 'external_gnss'
                                        ? 'No external-GNSS device assigned. Coordal Field (coming soon) for rover-backed sessions.'
                                        : 'No field device assigned to you. Register in Developer Console → Field equipment.'}
                                    </p>
                                  )}
                                  {publicMapBlocksExternalGnssSessions && (
                                    <div className="mb-2 rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2 text-[11px] text-amber-950 dark:border-amber-500/35 dark:bg-amber-500/10 dark:text-amber-100/95">
                                      External GNSS campaigns cannot start sessions on the web map yet.
                                    </div>
                                  )}
                                </>
                              )}
                              {!isAuthenticated && (
                                <div className="mt-2 rounded-xl border border-slate-200/80 dark:border-gray-600/55 bg-slate-50/95 dark:bg-gray-800/50 p-2.5 flex flex-col gap-2">
                                  <p className="text-[11px] text-slate-600 dark:text-gray-400 leading-relaxed">
                                    Sign in to sync sessions, your assigned devices, and live capture updates.
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      onClick={onRequireAuth}
                                      className="inline-flex items-center justify-center rounded-lg bg-gradient-to-b from-violet-600 to-violet-700 px-3 py-1.5 text-xs font-semibold text-white shadow-md shadow-violet-900/20 border border-violet-500/30"
                                    >
                                      Sign in
                                    </button>
                                    {onOpenSettings ? (
                                      <button
                                        type="button"
                                        onClick={() => onOpenSettings()}
                                        className="inline-flex items-center justify-center rounded-lg border border-slate-300/90 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                                      >
                                        Map settings
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                              )}
                              {isAuthenticated ? (
                                <div className="mt-2 border-t border-slate-200 pt-2 dark:border-gray-600/80">
                                  <button
                                    type="button"
                                    onClick={() => setRecentSessionsExpanded((v) => !v)}
                                    aria-expanded={recentSessionsExpanded}
                                    className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200/85 bg-white/80 px-2.5 py-1.5 text-left transition hover:bg-slate-100/90 dark:border-gray-600/55 dark:bg-gray-900/50 dark:hover:bg-gray-800/70"
                                  >
                                    <div className="flex min-w-0 flex-col gap-0.5">
                                      <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-600 dark:text-gray-300">
                                        Recent sessions
                                      </span>
                                      {!recentSessionsExpanded ? (
                                        <span className="text-[9px] font-medium text-slate-500 dark:text-gray-400">
                                          Team · latest 6
                                          {sessions.length > 0 ? ` · ${sessions.length} listed` : ''}
                                        </span>
                                      ) : null}
                                    </div>
                                    <ChevronDown
                                      className={clsx(
                                        'h-4 w-4 shrink-0 text-slate-500 transition dark:text-gray-400',
                                        recentSessionsExpanded && '-rotate-180',
                                      )}
                                      strokeWidth={2.25}
                                      aria-hidden
                                    />
                                  </button>
                                  {recentSessionsExpanded ? (
                                    <ul className="mt-1.5 space-y-1 rounded-lg border border-slate-200 bg-white p-1.5 dark:border-gray-600 dark:bg-gray-900/90">
                                      {sessions.slice(0, 6).map((s) => {
                                        const isMyActive =
                                          isSessionStatusActive(s.status) &&
                                          myUserId != null &&
                                          s.user_id === myUserId;
                                        return (
                                          <li key={s.id}>
                                            <button
                                              type="button"
                                              disabled={!isMyActive}
                                              onClick={() => {
                                                if (!isMyActive) return;
                                                void handleActivateActiveSession();
                                              }}
                                              className={clsx(
                                                'flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1 text-[11px] odd:bg-white/70 dark:odd:bg-gray-900/35',
                                                isMyActive
                                                  ? 'cursor-pointer hover:bg-emerald-50/90 dark:hover:bg-emerald-950/30'
                                                  : 'cursor-default',
                                              )}
                                              title={
                                                isMyActive
                                                  ? 'Select the zone for your active session'
                                                  : undefined
                                              }
                                            >
                                              <span className="min-w-0 truncate text-slate-700 dark:text-gray-200">
                                                {s.user_name || `User ${s.user_id}`}
                                                {isMyActive ? ' · you' : ''}
                                              </span>
                                              <span
                                                className={clsx(
                                                  'flex-shrink-0 rounded-full px-1.5 py-px text-[9px] font-bold uppercase',
                                                  isSessionStatusActive(s.status)
                                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300'
                                                    : 'bg-slate-200/80 text-slate-600 dark:bg-gray-700 dark:text-gray-300',
                                                )}
                                              >
                                                {s.status}
                                              </span>
                                            </button>
                                          </li>
                                        );
                                      })}
                                      {sessions.length === 0 && !loading ? (
                                        <li className="px-2 py-2 text-[11px] text-slate-500 text-center">No sessions yet.</li>
                                      ) : null}
                                    </ul>
                                  ) : null}
                                </div>
                              ) : null}
                            </>
                          ) : null}
                        </section>

                        <section ref={hardwareSectionRef} id="campaign-hardware" className="space-y-1.5 rounded-xl border border-slate-200 bg-slate-50 p-2 dark:border-gray-600 dark:bg-gray-800/95">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <Cpu className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                              <span className="text-xs font-semibold text-slate-800 dark:text-gray-100">Field source</span>
                            </div>
                            <span className="text-[9px] font-medium uppercase tracking-wider text-slate-400">Session hardware</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setHardwareExpanded((e) => !e)}
                            className={clsx(
                              'w-full flex items-center gap-3 rounded-2xl border px-3 py-2.5 text-left transition-all shadow-md',
                              'bg-gradient-to-br from-white to-slate-50/90 dark:from-gray-800/85 dark:to-gray-900/80',
                              hardwareNeedsAttention
                                ? 'border-amber-300/80 ring-1 ring-amber-200/50 dark:border-amber-500/40 dark:ring-amber-500/20'
                                : 'border-slate-200/80 dark:border-gray-700/55',
                            )}
                          >
                            <div
                              className={clsx(
                                'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border shadow-inner',
                                fieldDevicePolicy === 'none'
                                  ? 'bg-cyan-500/10 border-cyan-500/25'
                                  : fieldDevicePolicy === 'external_gnss'
                                    ? 'bg-rose-500/10 border-rose-500/25'
                                    : sessionDevicePool.length === 0
                                      ? 'bg-amber-500/10 border-amber-500/25'
                                      : 'bg-amber-500/10 border-amber-500/25',
                              )}
                            >
                              {fieldDevicePolicy === 'none' ? (
                                <Locate className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                              ) : (
                                <Cpu className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 dark:text-gray-500">
                                {fieldDevicePolicy === 'none' ? 'Default' : 'Registered device'}
                              </div>
                              <div className="text-sm font-semibold text-slate-900 dark:text-gray-50 truncate">
                                {hardwarePillTitle}
                              </div>
                              <div className="text-[10px] text-slate-500 dark:text-gray-400 truncate">{hardwarePillSubtitle}</div>
                            </div>
                            <ChevronDown
                              className={clsx(
                                'w-5 h-5 shrink-0 text-slate-400 transition-transform duration-200',
                                hardwareExpanded && 'rotate-180',
                              )}
                            />
                          </button>
                          {hardwareExpanded && (
                            <div className="rounded-xl border border-slate-200 bg-white p-2 dark:border-gray-600 dark:bg-gray-900/80 space-y-3">
                              {fieldDevicePolicy === 'none' && (
                                <div className="space-y-2.5">
                                  <div className="flex gap-3 rounded-xl border border-emerald-200/60 bg-emerald-50/50 dark:border-emerald-800/40 dark:bg-emerald-950/20 px-3 py-2.5">
                                    <span
                                      className="mt-0.5 h-2 w-2 rounded-full shrink-0 ring-2 ring-emerald-500/40"
                                      style={{ backgroundColor: '#22c55e' }}
                                      aria-hidden
                                    />
                                    <div className="min-w-0 flex-1 text-[11px] text-slate-600 dark:text-gray-300 leading-relaxed">
                                      <span className="font-semibold text-slate-800 dark:text-gray-100">
                                        Browser geolocation
                                      </span>{' '}
                                      is the default for this campaign. Session tracks use your device’s GPS (same as the map
                                      locate control).
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={handleFieldCapture}
                                    disabled={!fieldCaptureLive}
                                    className={clsx(
                                      'flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-xs font-semibold shadow-md transition',
                                      fieldCaptureLive
                                        ? 'bg-gradient-to-r from-cyan-600 to-teal-600 text-white hover:from-cyan-500 hover:to-teal-500'
                                        : 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-500',
                                    )}
                                    title={
                                      fieldCaptureLive
                                        ? 'Capture a point or GPS trace from your session, then label'
                                        : liveGpsPaused
                                          ? 'Resume Live GPS to capture field data'
                                          : !campaignIsActive
                                            ? 'Campaign is paused — capture is disabled'
                                            : 'Start a session to capture field data'
                                    }
                                  >
                                    <Crosshair className="h-4 w-4" />
                                    Capture &amp; label
                                  </button>
                                </div>
                              )}
                              {fieldDevicePolicy === 'assigned_device' && sessionDevicePool.length > 0 && (
                                <div className="space-y-1.5">
                                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                                    Your assigned devices
                                  </div>
                                  {sessionDevicePool.map((d) => {
                                    const p = devicePresence(d);
                                    const checked = selectedFieldDeviceId === d.id;
                                    return (
                                      <label
                                        key={d.id}
                                        className={clsx(
                                          'flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 transition-colors',
                                          checked
                                            ? 'border-cyan-400/50 bg-white dark:bg-gray-900/70 ring-1 ring-cyan-500/20'
                                            : 'border-slate-200/90 bg-white/70 hover:border-slate-300 dark:border-gray-700/60 dark:bg-gray-900/40',
                                        )}
                                      >
                                        <input
                                          type="radio"
                                          name="campaign-field-device"
                                          className="accent-cyan-600 h-4 w-4"
                                          checked={checked}
                                          onChange={() => setSelectedFieldDeviceId(d.id)}
                                        />
                                        <div className="min-w-0 flex-1">
                                          <div className="text-xs font-semibold text-slate-900 dark:text-gray-100 truncate">
                                            {d.device_tag}
                                            {d.gnss_receiver ? (
                                              <span className="font-normal text-slate-500"> · {d.gnss_receiver}</span>
                                            ) : null}
                                          </div>
                                          <div className={clsx('text-[10px]', p.tone)}>{p.label}</div>
                                        </div>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                              {fieldDevicePolicy === 'assigned_device' && sessionDevicePool.length === 0 && (
                                <p className="text-[11px] text-amber-800 dark:text-amber-300/90 leading-relaxed">
                                  No devices assigned to your account for this campaign. Add hardware in the Developer
                                  Console, then refresh.
                                </p>
                              )}
                              {fieldDevicePolicy === 'external_gnss' && (
                                <p className="text-[11px] text-slate-600 dark:text-gray-400 leading-relaxed">
                                  This campaign requires external GNSS. Public map sessions use browser location only, so
                                  starting here is disabled until Coordal Field is available.
                                </p>
                              )}
                              {devices.length > 0 && (fieldDevicePolicy !== 'assigned_device' || sessionDevicePool.length === 0) ? (
                                <div>
                                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-1.5">
                                    Campaign registry ({devices.length})
                                  </div>
                                  <ul className="space-y-1 pr-1 text-[10px]">
                                    {devices.map((d) => {
                                      const p = devicePresence(d);
                                      const mine = myUserId != null && d.assigned_to_id === myUserId;
                                      return (
                                        <li
                                          key={d.id}
                                          className="flex justify-between gap-2 rounded-lg border border-slate-200/60 bg-white/70 px-2 py-1 dark:border-gray-700/50 dark:bg-gray-900/40"
                                        >
                                          <span className="truncate text-slate-800 dark:text-gray-200">
                                            {d.device_tag}
                                            {mine ? (
                                              <span className="text-emerald-600 dark:text-emerald-400"> · you</span>
                                            ) : null}
                                          </span>
                                          <span className={clsx('flex-shrink-0', p.tone)}>{p.label}</span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              ) : null}
                            </div>
                          )}
                        </section>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div
                data-tour-target="campaign-tool-grid"
                className="relative flex w-full shrink-0 items-center justify-between gap-1 rounded-[1.35rem] border border-slate-200/70 bg-white/92 p-1.5 shadow-lg shadow-slate-900/10 ring-1 ring-inset ring-white/50 backdrop-blur-xl dark:border-gray-600/45 dark:bg-gray-900/92 dark:shadow-black/35 dark:ring-inset dark:ring-white/5 max-md:mx-0"
              >
                <div className="flex min-w-0 flex-1 items-center justify-center gap-1 pr-0.5">
                  <button type="button" className={compactMapToolClass} onClick={handleCampaignZoomOut} title="Zoom out">
                    <Minus className="h-[18px] w-[18px]" />
                  </button>
                  <button type="button" className={compactMapToolClass} onClick={handleCampaignZoomIn} title="Zoom in">
                    <Plus className="h-[18px] w-[18px]" />
                  </button>
                  <button
                    type="button"
                    className={compactMapToolClass}
                    onClick={handleCampaignLocate}
                    disabled={isLocating}
                    title="My location"
                  >
                    {isLocating ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-cyan-600 border-t-transparent dark:border-cyan-400" />
                    ) : (
                      <Locate className="h-[18px] w-[18px]" />
                    )}
                  </button>
                  <button
                    type="button"
                    className={compactMapToolClass}
                    onClick={handleCampaignFitWorkspace}
                    disabled={!zonesFc?.features?.length && !liveFc?.features?.length}
                    title="Fit campaign on map"
                  >
                    <Expand className="h-[18px] w-[18px]" />
                  </button>
                </div>
                <div
                  className="mx-1 h-7 w-px shrink-0 bg-gradient-to-b from-transparent via-slate-200/95 to-transparent dark:via-gray-500/50"
                  aria-hidden
                />
                <div ref={campaignMoreToolsRef} className="relative shrink-0">
                  <button
                    type="button"
                    className={clsx(
                      compactMapToolClass,
                      campaignMoreToolsOpen && compactMapToolClassMoreOpen,
                    )}
                    aria-expanded={campaignMoreToolsOpen}
                    aria-haspopup="true"
                    onClick={() => setCampaignMoreToolsOpen((prev: boolean) => !prev)}
                    title="More tools"
                  >
                    <MoreHorizontal className="h-[18px] w-[18px]" />
                  </button>
                  {campaignMoreToolsOpen ? (
                    <div
                      className="absolute right-0 bottom-[calc(100%+8px)] z-[60] w-[min(220px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200/75 bg-white/95 p-1 shadow-2xl shadow-slate-900/12 backdrop-blur-xl dark:border-gray-500/50 dark:bg-gray-900 dark:shadow-black/60"
                      role="menu"
                    >
                      <button
                        type="button"
                        role="menuitem"
                        className={moreMenuItemClass}
                        onClick={() => {
                          setCampaignMoreToolsOpen(false);
                          if (onOpenProfile) onOpenProfile();
                          else onRequireAuth();
                        }}
                      >
                        <User className="h-4 w-4 opacity-80" />
                        Profile
                      </button>
                      {onGoHome && !homeOverlayMode ? (
                        <button
                          type="button"
                          role="menuitem"
                          className={moreMenuItemClass}
                          onClick={() => {
                            setCampaignMoreToolsOpen(false);
                            onGoHome();
                          }}
                        >
                          <Home className="h-4 w-4 opacity-80" />
                          Map home
                        </button>
                      ) : null}
                      {onOpenSettings ? (
                        <button
                          type="button"
                          role="menuitem"
                          className={moreMenuItemClass}
                          onClick={() => {
                            setCampaignMoreToolsOpen(false);
                            onOpenSettings();
                          }}
                        >
                          <Settings className="h-4 w-4 opacity-80" />
                          Settings
                        </button>
                      ) : null}
                      {MARKETING_URL ? (
                        <a
                          href={FIELD_COLLECTION_DOCS_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          role="menuitem"
                          className={moreMenuItemClass}
                          onClick={() => setCampaignMoreToolsOpen(false)}
                        >
                          <BookOpen className="h-4 w-4 opacity-80" />
                          Docs
                        </a>
                      ) : null}
                      <button
                        type="button"
                        role="menuitem"
                        className={moreMenuItemClass}
                        onClick={() => {
                          setCampaignMoreToolsOpen(false);
                          void load();
                        }}
                      >
                        <RefreshCw className={clsx('h-4 w-4 opacity-80', loading && 'animate-spin')} />
                        Refresh
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className={moreMenuItemClass}
                        onClick={() => {
                          setCampaignMoreToolsOpen(false);
                          hardwareSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                      >
                        <Cpu className="h-4 w-4 opacity-80" />
                        Field hardware
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        {!hideSessionDock ? (
        <div
          className={clsx(
            'pointer-events-auto w-full max-w-full md:max-w-[min(400px,calc(100vw-1.5rem))]',
            homeOverlayMode
              ? 'rounded-2xl border border-slate-200/90 bg-white/95 p-2 shadow-lg backdrop-blur-xl dark:border-gray-600/60 dark:bg-gray-900/95'
              : 'flex flex-row items-stretch justify-end gap-2 max-md:px-1',
          )}
        >
          {homeOverlayMode ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                {onReturnToWorkspace ? (
                  <button
                    type="button"
                    onClick={() => onReturnToWorkspace()}
                    className={dockChipNeutral}
                    title="Return to campaign workspace"
                  >
                    <Layers className="h-4 w-4" />
                    Campaign
                  </button>
                ) : null}
                <button
                  type="button"
                  data-tour-target="campaign-session-actions"
                  disabled={sessionToggleDisabled}
                  onClick={handleSessionToggle}
                  className={clsx(
                    'flex-1 min-w-0',
                    myActiveSession ? dockChipSessionStop : dockChipSessionStart,
                  )}
                  title={
                    !myActiveSession && !geoReady && fieldDevicePolicy === 'none'
                      ? 'Allow browser location for the best start position.'
                      : undefined
                  }
                >
                  {myActiveSession ? (
                    <>
                      <Square className="h-4 w-4 fill-current" />
                      End session
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      Start session
                    </>
                  )}
                </button>
                {myActiveSession ? (
                  <button
                    type="button"
                    onClick={() => void handleActivateActiveSession()}
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800 transition hover:bg-emerald-100 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-900/50"
                    title="Restore session zone on map"
                  >
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" aria-hidden />
                    Live
                  </button>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {onOpenSettings ? (
                  <button
                    type="button"
                    onClick={() => onOpenSettings()}
                    className={clsx(dockChipNeutral, 'shrink-0 px-2.5')}
                    title="Settings"
                  >
                    <Settings className="h-4 w-4" />
                  </button>
                ) : null}
                {onOpenContribution ? (
                  <button
                    type="button"
                    onClick={handleFieldCapture}
                    disabled={!fieldCaptureLive}
                    className={clsx(
                      'flex-1 min-w-0',
                      fieldCaptureLive
                        ? clsx(
                            dockChipBase,
                            'border-cyan-200 bg-cyan-50 text-cyan-900 hover:bg-cyan-100 dark:border-cyan-800/50 dark:bg-cyan-950/40 dark:text-cyan-100 dark:hover:bg-cyan-900/50',
                          )
                        : clsx(dockChipNeutral, 'cursor-not-allowed opacity-50 hover:bg-white dark:hover:bg-gray-800'),
                    )}
                    title={
                      fieldCaptureLive
                        ? 'Capture a point or GPS trace, then label'
                        : liveGpsPaused
                          ? 'Resume Live GPS to capture'
                          : !campaignIsActive
                            ? 'Campaign paused — capture disabled'
                            : 'Start a session first'
                    }
                  >
                    <Crosshair className="h-4 w-4 shrink-0" />
                    <span className="truncate">Capture &amp; label</span>
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="flex w-full items-center gap-2 max-md:px-1">
              {onGoHome ? (
                <button
                  type="button"
                  data-tour-target="campaign-go-home"
                  onClick={() => onGoHome()}
                  className={clsx('shrink-0', dockChipExpand, dockChipGlow)}
                  title="Explore map while session stays active"
                >
                  <Home className="h-4 w-4" />
                  Home
                </button>
              ) : null}
              <button
                type="button"
                data-tour-target="campaign-session-actions"
                disabled={sessionToggleDisabled}
                onClick={handleSessionToggle}
                className={clsx(
                  'shrink-0',
                  myActiveSession ? dockChipSessionStop : dockChipSessionStart,
                )}
                title={
                  !myActiveSession && !geoReady && fieldDevicePolicy === 'none'
                    ? 'Allow browser location for the best start position.'
                    : undefined
                }
              >
                {myActiveSession ? (
                  <>
                    <Square className="h-3.5 w-3.5 fill-current" />
                    End
                  </>
                ) : (
                  <>
                    <Play className="h-3.5 w-3.5" />
                    Start
                  </>
                )}
              </button>
              {myActiveSession ? (
                <button
                  type="button"
                  onClick={() => void handleActivateActiveSession()}
                  className="inline-flex h-9 shrink-0 items-center gap-1 rounded-xl border border-emerald-200 bg-emerald-50 px-2 text-[10px] font-bold uppercase tracking-wide text-emerald-800 transition hover:bg-emerald-100 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-900/50"
                  title="Restore session zone on map"
                >
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" aria-hidden />
                  Live
                </button>
              ) : null}
              <button
                type="button"
                data-tour-target="campaign-dock-pill"
                onClick={() => onToggleCollapse?.()}
                disabled={!onToggleCollapse}
                className={clsx(
                  'min-w-0 flex-1 justify-start text-left',
                  dockChipExpand,
                  dockChipGlow,
                )}
              >
                <Layers className="h-4 w-4 shrink-0 opacity-80" />
                <span className="truncate">{campaignName || 'Campaign'}</span>
                {collapsed ? (
                  <ChevronUp className="ml-auto h-4 w-4 shrink-0 opacity-70" />
                ) : (
                  <ChevronDown className="ml-auto h-4 w-4 shrink-0 opacity-70" />
                )}
              </button>
            </div>
          )}
        </div>
        ) : null}

        <AnimatePresence>
          {confirmEndSessionOpen ? (
            <motion.div
              className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 bg-slate-950/40 backdrop-blur-sm dark:bg-gray-950/55 pointer-events-auto"
              role="dialog"
              aria-modal="true"
              aria-labelledby="campaign-end-session-title"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={() => setConfirmEndSessionOpen(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 14 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98, y: 10 }}
                transition={{ type: 'spring', damping: 28, stiffness: 360 }}
                className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200/75 bg-white/95 shadow-2xl shadow-slate-900/10 backdrop-blur-xl dark:border-gray-500/50 dark:bg-gray-900 dark:shadow-black/50"
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className="h-1 w-full bg-gradient-to-r from-rose-500 via-amber-500 to-orange-500 opacity-90 dark:opacity-80"
                  aria-hidden
                />
                <div className="p-5">
                  <div className="flex gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-100/95 dark:bg-rose-500/12 ring-1 ring-rose-300/40 dark:ring-rose-400/25">
                      <AlertTriangle className="h-5 w-5 text-rose-700 dark:text-rose-400" />
                    </div>
                    <div>
                      <h2
                        id="campaign-end-session-title"
                        className="text-base font-semibold text-slate-900 dark:text-gray-50"
                      >
                        End collection session?
                      </h2>
                      <p className="mt-2 text-sm text-slate-600 dark:text-gray-300/95 leading-relaxed">
                        Stopping ends your live session. New captures will no longer link to this campaign until you
                        start again. Team sync for this session will close.
                      </p>
                    </div>
                  </div>
                  <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      className="rounded-xl border border-slate-200/95 bg-white/90 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50/95 dark:border-gray-600/70 dark:bg-gray-800/90 dark:text-gray-100 dark:hover:bg-gray-700/85"
                      onClick={() => setConfirmEndSessionOpen(false)}
                    >
                      Keep session
                    </button>
                    <button
                      type="button"
                      className="rounded-xl bg-gradient-to-r from-rose-600 to-rose-700 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-rose-900/15 hover:from-rose-500 hover:to-rose-600"
                      onClick={confirmEndSession}
                    >
                      Stop session
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <AnimatePresence>
          {confirmExitCampaignOpen && onExitCampaign ? (
            <motion.div
              className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 bg-slate-950/40 backdrop-blur-sm dark:bg-gray-950/55 pointer-events-auto"
              role="dialog"
              aria-modal="true"
              aria-labelledby="campaign-exit-title"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={() => setConfirmExitCampaignOpen(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 14 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98, y: 10 }}
                transition={{ type: 'spring', damping: 28, stiffness: 360 }}
                className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200/75 bg-white/95 shadow-2xl shadow-slate-900/10 backdrop-blur-xl dark:border-gray-500/50 dark:bg-gray-900 dark:shadow-black/50"
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className="h-1 w-full bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 opacity-90 dark:opacity-80"
                  aria-hidden
                />
                <div className="p-5">
                  <div className="flex gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100/95 dark:bg-amber-500/12 ring-1 ring-amber-300/40 dark:ring-amber-400/25">
                      <AlertTriangle className="h-5 w-5 text-amber-700 dark:text-amber-400" />
                    </div>
                    <div>
                      <h2
                        id="campaign-exit-title"
                        className="text-base font-semibold text-slate-900 dark:text-gray-50"
                      >
                        Leave campaign?
                      </h2>
                      <p className="mt-2 text-sm text-slate-600 dark:text-gray-300/95 leading-relaxed">
                        {myActiveSession ? (
                          <>
                            You have an{' '}
                            <strong className="font-semibold text-slate-800 dark:text-gray-100">
                              active collection session
                            </strong>
                            . Closing this view does{' '}
                            <strong className="font-semibold text-slate-800 dark:text-gray-100">not</strong> end it —
                            use{' '}
                            <strong className="font-semibold text-slate-800 dark:text-gray-100">End</strong> in the
                            dock when you are finished, so the session is not left running unattended.
                          </>
                        ) : (
                          <>
                            You will return to the explore map. If you start a session later, end it from the campaign
                            workspace when you are done collecting.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      className="rounded-xl border border-slate-200/95 bg-white/90 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50/95 dark:border-gray-600/70 dark:bg-gray-800/90 dark:text-gray-100 dark:hover:bg-gray-700/85"
                      onClick={() => setConfirmExitCampaignOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="rounded-xl bg-gradient-to-r from-cyan-600 to-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-cyan-900/15 hover:from-cyan-500 hover:to-teal-500 dark:from-cyan-500 dark:to-teal-500 dark:shadow-cyan-950/35 dark:hover:from-cyan-400 dark:hover:to-teal-400"
                      onClick={confirmExitCampaign}
                    >
                      Leave campaign
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div >
    );
  }
}
